import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import { useTheme } from 'next-themes';
import userDataService from '../../../lib/services/userDataService';
import {
  fetchAllUnifiedPlanningFeed,
  fetchAllUnifiedPlanningFeedLight,
  fetchUnifiedPlanningFeedLight,
  fetchAllShortTermLetsLight,
  fetchPlanningMapFeedSnapshot,
  fetchShortTermLetsLightSnapshot,
} from '../../../lib/services/applicationsApiService';
import {
  isPlanningFeedAddedToday,
  isPlanningFeedAddedYesterday,
} from '../../../utils/addedToday';
import { buildPlanningApplicationPath } from '../../../lib/planningApplicationUrl';
import { isPlanningDocumentViewerUrl } from '../../../utils/openPlanningDoc';
import {
  AGGREGATE_COUNCIL_SLUG,
  COUNCILS,
  DEFAULT_COUNCIL_SLUG,
  applicationIdentityKey,
  getCouncilConfig,
  isAggregateCouncilScope,
  LONDON_REGION_CONFIG,
} from '../../../lib/councils';
import { isLondonScope } from '../../../lib/services/londonPlanningService';
import { usesRetainedLondonDelivery } from '../../../lib/planning/londonSharedDelivery';
import { sanitizePublicPlanningRecord } from '../../../lib/planning/publicReleasePolicy';
import { getSearchablePlanningApplicationText } from '../../../lib/planning/searchText';
import {
  SHORT_LETS_CATEGORY_ID,
  isShortLetsSearchQuery,
} from '../../../lib/planning/shortLetsSearch';
import { parsePlanningSearchIntent } from '../../../lib/planning/searchIntent';
import { usePlanningFeedSummary } from '../../../lib/planning/usePlanningFeedSummary';
import {
  fetchPlanningFeedCounts,
  getPlanningCountForScope,
} from '../../../lib/planning/planningFeedCounts';
import { patchPlanningQuery } from '../../../lib/planning/queryPatch';
// Re-enable URL syncing with throttling to avoid shallow router churn and cursor flicker.
const URL_SYNC_ENABLED = true;
const URL_SYNC_DEBOUNCE_MS = 500;

// Import newspaper config utilities
import {
  // filterOptions,
  // weatherForecasts,
  // historicalFacts,
  // editorsNotes,
  // classifieds,
  // vintageAdverts
} from "../../../utils/newspaperConfig";

// Note: Smart grouping utilities removed - now using backend group_id

// Import categorization helper
import { 
  applyApiOnlyCategorization,
  normalizeCategory,
  getCategorizationStats,
  apiOnlyCategorizeApplication
} from "../../../utils/categorizationHelper";

// Create context
const PlanningContext = createContext();

const debugEnabled = process.env.NEXT_PUBLIC_PLANNING_DEBUG === 'true';
const DEMO_MODE_ENABLED = process.env.NEXT_PUBLIC_DEMO_MODE === '1';
const debugLog = (...args) => {
  if (!debugEnabled) return;
  console.log(...args);
};

const debugWarn = (...args) => {
  if (!debugEnabled) return;
  console.warn(...args);
};

const summarizeRecoverableError = (error) => {
  if (!error) return { message: 'Unknown error' };
  return {
    name: error.name || 'Error',
    message: error.message || String(error),
    status: error.status,
    kind: error.kind,
    url: error.url,
  };
};

const PLANNING_SEARCH_STREET_TERMS = [
  'street',
  'road',
  'avenue',
  'ave',
  'lane',
  'ln',
  'place',
  'terrace',
  'crescent',
  'cres',
  'square',
  'sq',
  'mews',
  'wynd',
  'close',
  'row',
  'way',
  'walk',
  'drive',
  'dr',
  'park',
  'grove',
  'hill',
  'green',
  'gardens',
  'court',
  'circus',
  'parade',
];
const PLANNING_SEARCH_POSTCODE_REGEX = /[a-z]{1,2}\d[a-z\d]?\s?\d[a-z]{2}/i;
const escapePlanningSearchToken = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isPlanningAddressSearchToken = (token) => {
  if (!token) return false;
  if (PLANNING_SEARCH_POSTCODE_REGEX.test(token)) return true;
  if (/^\d+[a-z]?$/.test(token)) return true;
  return PLANNING_SEARCH_STREET_TERMS.some((streetTerm) => token.includes(streetTerm));
};
const buildPlanningSearchMatchers = (rawQuery) => String(rawQuery || '')
  .toLowerCase()
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .map((token) => ({
    token,
    addressish: isPlanningAddressSearchToken(token),
    pattern: new RegExp(`(^|[^a-z0-9])${escapePlanningSearchToken(token)}s?([^a-z0-9]|$)`),
  }));

const firstPlanningQueryValue = (value) => {
  if (Array.isArray(value)) return value[0] || '';
  return typeof value === 'string' ? value : '';
};

const readPlanningQueryValue = (query, key) => {
  const routerValue = firstPlanningQueryValue(query?.[key]);
  if (routerValue) return routerValue;
  if (typeof window === 'undefined') return '';

  try {
    return new URLSearchParams(window.location.search).get(key) || '';
  } catch (_) {
    return '';
  }
};

const parsePlanningCategoryQueryValues = (value = '') => String(value || '')
  .split(',')
  .map((part) => {
    const trimmed = part.trim();
    if (!trimmed) return '';
    try {
      return decodeURIComponent(trimmed);
    } catch (_) {
      return trimmed;
    }
  })
  .filter(Boolean);

const readPlanningCategoryQueryValues = (query, searchValue = '') => {
  const explicitCategories =
    readPlanningQueryValue(query, 'categories') || readPlanningQueryValue(query, 'category');
  const categories = parsePlanningCategoryQueryValues(explicitCategories);
  if (categories.length > 0) return categories;
  return isShortLetsSearchQuery(searchValue) ? [SHORT_LETS_CATEGORY_ID] : [];
};

const readPlanningSearchIntentQueryValues = (query, councilSlug) => {
  const rawSearch = readPlanningQueryValue(query, 'search') || readPlanningQueryValue(query, 'q');
  if (usesRetainedLondonDelivery(councilSlug)) return { rawSearch, search: rawSearch };
  const intent = parsePlanningSearchIntent(rawSearch);
  const search = intent.recognized ? intent.residualTokens.join(' ') : rawSearch;
  return { rawSearch, search, intent };
};

const ROUTE_QUERY_KEYS = new Set(['reference', 'referenceSegments', 'councilCode', 'areaSegments']);

const stripRouteQueryKeys = (query = {}) => {
  const clean = { ...query };
  ROUTE_QUERY_KEYS.forEach((key) => {
    delete clean[key];
  });
  return clean;
};

const planningUpdatesReferenceFromPath = (pathname = '') => {
  const match = String(pathname || '').match(/^\/planning-updates\/(.+)$/);
  if (!match) return null;

  const segments = match[1]
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch (_) {
        return segment;
      }
    })
    .filter(Boolean);

  if (segments.length === 0) return null;

  const council = getCouncilConfig(segments[0]);
  if (council) {
    return segments.length > 1 ? segments.slice(1).join('/') : null;
  }

  return segments[0] === 'london' ? null : segments.join('/');
};

const PLANNING_REGION_ROUTE_EVENT = 'cityscope:planning-region-route';

const AGGREGATE_COUNCIL_CONFIG = {
  slug: AGGREGATE_COUNCIL_SLUG,
  shortCode: AGGREGATE_COUNCIL_SLUG,
  displayName: 'Scotland',
  officialName: 'Scottish planning authorities',
  defaultCenter: { lng: -3.188267, lat: 55.953251 },
  publicEnabled: true,
  legacyDefault: true,
};

const planningCouncilSlugFromPath = (pathname = '') => {
  const path = String(pathname || '').split('?')[0].split('#')[0] || '';
  if (!path.startsWith('/planning-updates') && !path.startsWith('/planning-map')) {
    return null;
  }

  if (path === '/planning-updates') {
    return AGGREGATE_COUNCIL_SLUG;
  }
  if (path === '/planning-map') {
    return AGGREGATE_COUNCIL_SLUG;
  }

  const parts = path.split('/').filter(Boolean);
  const routeCouncil = parts[1];
  return routeCouncil === 'london' ? 'london' : getCouncilConfig(routeCouncil)?.slug || DEFAULT_COUNCIL_SLUG;
};

const planningAreaIdFromPath = (pathname = '') => {
  const path = String(pathname || '').split('?')[0].split('#')[0] || '';
  if (!path.startsWith('/planning-updates/') && !path.startsWith('/planning-map/')) {
    return null;
  }

  const parts = path.split('/').filter(Boolean);
  const council = getCouncilConfig(parts[1]);
  const placeSlug = parts[2];
  if (!council || !placeSlug) return null;
  return `${council.slug}:${String(placeSlug).trim().toLowerCase()}`;
};

const formatLocalDateKey = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const londonDateTimePartsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hour12: false,
});

const getLondonTodayWindowKey = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';

  const parts = londonDateTimePartsFormatter.formatToParts(value);
  const read = (type) => parts.find((part) => part.type === type)?.value || '';
  const hour = Number.parseInt(read('hour'), 10);
  const date = `${read('year')}-${read('month')}-${read('day')}`;

  if (!date || Number.isNaN(hour)) return '';
  // The Today feed window starts at 06:00 Europe/London. Include the phase so
  // an open tab refreshes when the morning ingestion window begins as well as
  // when the calendar date changes at midnight.
  return `${date}:${hour >= 6 ? 'active' : 'pre-cutoff'}`;
};

const getDateSliceUrlParams = (query) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const rawDateParam =
    readPlanningQueryValue(query, 'date') || readPlanningQueryValue(query, 'scrapedDate');

  return {
    addedParam: readPlanningQueryValue(query, 'added').toLowerCase(),
    normalizedDateParam: /^\d{4}-\d{2}-\d{2}$/.test(rawDateParam) ? rawDateParam : '',
    todayDateString: formatLocalDateKey(today),
    yesterdayDateString: formatLocalDateKey(yesterday),
  };
};

const getPlanningUpdatesReturnUrl = () => {
  if (typeof window === 'undefined') return '/planning-updates';
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    if (/^#ref=/i.test(url.hash)) {
      url.hash = '';
    }
    const query = url.searchParams.toString();
    return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
  } catch (_) {
    return '/planning-updates';
  }
};

const APPLICATIONS_CACHE_KEY = 'cs_apps_cache_v16';
const APPLICATIONS_CACHE_PROFILE = 'rich-media-ai-v2-today-window';
const APPLICATIONS_REFRESH_SKIP_MS = 1000 * 60; // 1 minute
const FULL_DATASET_FETCH_COOLDOWN_MS = 1000 * 60; // 1 minute
const FULL_DATASET_FETCH_PAGE_SIZE = 250;
const LATEST_PAGE_REFRESH_PAGE_SIZE = 500;
const LATEST_PAGE_REFRESH_TIMEOUT_MS = 12000;
const SHORT_LETS_FETCH_PAGE_SIZE = 250;
const SHORT_LETS_FETCH_MAX_PAGES = 40;
// Keep the background feed walk parallel enough to populate quickly without
// flooding the API.
const FULL_DATASET_FETCH_PAGE_CONCURRENCY = 2;
const FULL_DATASET_FETCH_MAX_PAGES = 128;
const PREFER_PLANNING_SNAPSHOT_REFRESH =
  process.env.NEXT_PUBLIC_PREFER_PLANNING_SNAPSHOT_REFRESH !== '0';
const PREFER_FORM_INDEX_SNAPSHOT =
  process.env.NEXT_PUBLIC_PREFER_FORM_INDEX_SNAPSHOT !== '0';
const PLANNING_MAP_SNAPSHOT_FALLBACK_TIMEOUT_MS = 8000;
const FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS = 90000;
const FAST_COUNCIL_INDEX_TIMEOUT_MS = 12000;
const EXPECTED_COUNT_FETCH_TIMEOUT_MS = 1800;
const BACKGROUND_DATA_APPLY_INTERVAL_MS = 1400;
const BACKGROUND_DATA_APPLY_MIN_DELTA = 500;
const FORM_INDEX_IDLE_DELAY_MS = 2500;
const FULL_DATASET_INTENT_EVENT = 'cityscope:planning-full-dataset-intent';
const INITIAL_VISIBLE_GROUP_COUNT = 12;
const VISIBLE_GROUP_BATCH_SIZE = 6;
const EMPTY_APPLICATIONS = [];
const MIN_GROUPING_SOURCE_APPLICATIONS = 288;
const GROUPING_SOURCE_APPLICATIONS_PER_VISIBLE_GROUP = 24;
const GROUPING_SOURCE_GROWTH_STEP = 192;
const APPLICATIONS_CACHE_DB_NAME = 'cityscope_cache';
const APPLICATIONS_CACHE_DB_VERSION = 1;
const APPLICATIONS_CACHE_STORE_NAME = 'kv';
const APPLICATIONS_CACHE_IDB_RECORD_KEY = 'planning:applications_full_v16';
const APPLICATIONS_CACHE_COUNT_TOLERANCE = 30;

const sharedFullDatasetByCouncil = new Map();
const sharedFullDatasetFetchPromiseByCouncil = new Map();
const sharedLatestPageRefreshPromiseByCouncil = new Map();
const sharedShortLetsByCouncil = new Map();
const sharedShortLetsFetchPromiseByCouncil = new Map();
let applicationsCacheDbPromise = null;

const normalizeCouncilScope = (value) => (
  value === 'london' ? 'london' : isAggregateCouncilScope(value)
    ? AGGREGATE_COUNCIL_SLUG
    : getCouncilConfig(value)?.slug || DEFAULT_COUNCIL_SLUG
);
const getCouncilApiParam = (value) => (
  isAggregateCouncilScope(value) ? AGGREGATE_COUNCIL_SLUG : normalizeCouncilScope(value)
);
const normalizeAreaScopeId = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return raw ? raw : null;
};
const getApplicationsScopeKey = (councilSlug, areaId = null) => {
  const councilKey = normalizeCouncilScope(councilSlug);
  const areaKey = normalizeAreaScopeId(areaId);
  return areaKey ? `${councilKey}:${areaKey}` : councilKey;
};
const getPublicCouncilSlugs = () =>
  Object.values(COUNCILS)
    .filter((council) => council?.publicEnabled)
    .map((council) => council.slug);
const getApplicationsStorageKey = (councilSlug, areaId = null) =>
  `${APPLICATIONS_CACHE_KEY}:${getApplicationsScopeKey(councilSlug, areaId)}`;
const getApplicationsIndexedDbKey = (councilSlug, areaId = null) =>
  `${APPLICATIONS_CACHE_IDB_RECORD_KEY}:${getApplicationsScopeKey(councilSlug, areaId)}`;
const getFavoritesStorageKey = (councilSlug) =>
  `cs_favorites:${normalizeCouncilScope(councilSlug)}`;

const hasRichApplicationMediaShape = (application) => {
  if (!application || typeof application !== 'object') return false;
  if (typeof application.image_url === 'string' && application.image_url.trim() !== '') return true;
  if (typeof application.thumbnail_url === 'string' && application.thumbnail_url.trim() !== '') return true;
  if (!Array.isArray(application.documents)) return false;
  return application.documents.some((document) => {
    if (!document || typeof document !== 'object') return false;
    return [
      document.thumbnail_url,
      document.thumbnail,
      document.preview_url,
      document.preview,
      document.image_url,
      document.doc_link,
      document.document_url,
      document.url,
    ].some((value) => typeof value === 'string' && value.trim() !== '');
  });
};

const isRichApplicationsDataset = (data) =>
  Array.isArray(data) && data.length > 0 && data.some(hasRichApplicationMediaShape);

const hasMeaningfulText = (value) =>
  typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== 'null';

const isPlanningFeedRecord = (application) => {
  const recordType = String(application?.record_type || '').trim().toLowerCase();
  const dataSource = String(application?.data_source || '').trim().toLowerCase();
  if (recordType === 'short_term_let' || dataSource === 'short_term_let') return false;
  return true;
};

const hasAiEnrichmentShape = (application) => {
  if (!application || typeof application !== 'object') return false;
  const scaleRating = Number(application.scale_rating);
  return (
    hasMeaningfulText(application.ai_summary) &&
    application.scale_rating !== null &&
    application.scale_rating !== undefined &&
    Number.isFinite(scaleRating) &&
    hasMeaningfulText(application.category)
  );
};

const getAiEnrichmentStats = (data = []) => {
  if (!Array.isArray(data)) return { total: 0, missing: 0 };
  let total = 0;
  let missing = 0;
  data.forEach((application) => {
    if (!isPlanningFeedRecord(application)) return;
    total += 1;
    if (!hasAiEnrichmentShape(application)) {
      missing += 1;
    }
  });
  return { total, missing };
};

const hasAcceptableAiEnrichmentCoverage = (data = []) => {
  const { total, missing } = getAiEnrichmentStats(data);
  if (total < 50) return true;
  const allowedMissing = Math.max(10, Math.ceil(total * 0.03));
  return missing <= allowedMissing;
};

const isCacheableApplicationsDataset = (data) =>
  isRichApplicationsDataset(data) && hasAcceptableAiEnrichmentCoverage(data);

const normalizeApplicationsCacheEntry = (parsed) => {
  const todayWindowKey = getLondonTodayWindowKey(new Date());
  if (
    !parsed ||
    parsed.profile !== APPLICATIONS_CACHE_PROFILE ||
    parsed.todayWindowKey !== todayWindowKey ||
    !isCacheableApplicationsDataset(parsed.data)
  ) {
    return null;
  }
  const timestamp = Number(parsed.timestamp);
  if (!Number.isFinite(timestamp)) return null;
  return { data: parsed.data, timestamp, todayWindowKey };
};

const normalizeReferenceDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getHotApplicationsCacheEntry = (councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  const entry = sharedFullDatasetByCouncil.get(getApplicationsScopeKey(councilSlug, areaId));
  if (
    !entry ||
    entry.todayWindowKey !== getLondonTodayWindowKey(new Date()) ||
    !isCacheableApplicationsDataset(entry.data)
  ) {
    return null;
  }
  if (!Number.isFinite(entry.timestamp) || entry.timestamp <= 0) {
    return null;
  }
  return {
    data: entry.data,
    timestamp: entry.timestamp,
    todayWindowKey: entry.todayWindowKey,
  };
};

const promoteHotApplicationsCacheEntry = (entry, councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  if (!entry || !isCacheableApplicationsDataset(entry.data)) return;
  if (!Number.isFinite(entry.timestamp)) return;
  const councilKey = getApplicationsScopeKey(councilSlug, areaId);
  const current = sharedFullDatasetByCouncil.get(councilKey);
  if (!current || entry.timestamp >= current.timestamp) {
    sharedFullDatasetByCouncil.set(councilKey, {
      data: entry.data,
      timestamp: entry.timestamp,
      todayWindowKey: entry.todayWindowKey,
    });
  }
};

const canUseIndexedDb = () =>
  typeof window !== 'undefined' &&
  typeof window.indexedDB !== 'undefined' &&
  window.indexedDB !== null;

const closeApplicationsCacheDb = (db) => {
  try {
    db?.close?.();
  } catch (_) {
    /* noop */
  }
};

const openApplicationsCacheDb = async () => {
  if (!canUseIndexedDb()) return null;
  if (applicationsCacheDbPromise) return applicationsCacheDbPromise;

  applicationsCacheDbPromise = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(
        APPLICATIONS_CACHE_DB_NAME,
        APPLICATIONS_CACHE_DB_VERSION
      );

      request.onupgradeneeded = (event) => {
        try {
          const db = event?.target?.result || request.result;
          if (!db || !db.objectStoreNames) {
            closeApplicationsCacheDb(db);
            return;
          }
          if (!db.objectStoreNames.contains(APPLICATIONS_CACHE_STORE_NAME)) {
            db.createObjectStore(APPLICATIONS_CACHE_STORE_NAME, { keyPath: 'key' });
          }
        } catch (_) {
          closeApplicationsCacheDb(event?.target?.result || request.result);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        if (!db || !db.objectStoreNames?.contains(APPLICATIONS_CACHE_STORE_NAME)) {
          applicationsCacheDbPromise = null;
          closeApplicationsCacheDb(db);
          resolve(null);
          return;
        }
        db.onversionchange = () => {
          closeApplicationsCacheDb(db);
          applicationsCacheDbPromise = null;
        };
        resolve(db);
      };

      request.onerror = () => {
        applicationsCacheDbPromise = null;
        resolve(null);
      };

      request.onblocked = () => {
        applicationsCacheDbPromise = null;
        resolve(null);
      };
    } catch (_) {
      applicationsCacheDbPromise = null;
      resolve(null);
    }
  });

  return applicationsCacheDbPromise;
};

const readApplicationsCacheEntryFromIndexedDb = async (councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  const db = await openApplicationsCacheDb();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(APPLICATIONS_CACHE_STORE_NAME, 'readonly');
      const store = tx.objectStore(APPLICATIONS_CACHE_STORE_NAME);
      const request = store.get(getApplicationsIndexedDbKey(councilSlug, areaId));

      request.onsuccess = () => resolve(normalizeApplicationsCacheEntry(request.result));
      request.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  });
};

const writeApplicationsCacheToIndexedDb = async (
  data,
  timestamp,
  councilSlug = DEFAULT_COUNCIL_SLUG,
  areaId = null
) => {
  if (!Array.isArray(data) || !Number.isFinite(timestamp)) return;
  const todayWindowKey = getLondonTodayWindowKey(new Date());
  const db = await openApplicationsCacheDb();
  if (!db) return;

  try {
    await new Promise((resolve) => {
      const tx = db.transaction(APPLICATIONS_CACHE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(APPLICATIONS_CACHE_STORE_NAME);
      const request = store.put({
        key: getApplicationsIndexedDbKey(councilSlug, areaId),
        data,
        timestamp,
        profile: APPLICATIONS_CACHE_PROFILE,
        todayWindowKey,
      });

      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    });
  } catch (_) {
    /* ignore IndexedDB write failures */
  }
};

const readApplicationsCacheEntry = (councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getApplicationsStorageKey(councilSlug, areaId));
    if (!raw) return null;
    return normalizeApplicationsCacheEntry(JSON.parse(raw));
  } catch (_) {
    return null;
  }
};

const writeApplicationsCache = (data, councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  if (!isCacheableApplicationsDataset(data)) return;
  const timestamp = Date.now();
  const todayWindowKey = getLondonTodayWindowKey(new Date(timestamp));
  const councilKey = getApplicationsScopeKey(councilSlug, areaId);

  // Keep a module-level hot cache so route-to-route remounts can reuse the latest
  // dataset without immediately re-fetching.
  sharedFullDatasetByCouncil.set(councilKey, { data, timestamp, todayWindowKey });

  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getApplicationsStorageKey(councilSlug, areaId),
      JSON.stringify({
        data,
        timestamp,
        profile: APPLICATIONS_CACHE_PROFILE,
        todayWindowKey,
      })
    );
  } catch (_) {
    /* ignore write errors (e.g., quota exceeded) */
  }

  // localStorage can fail due to size; IndexedDB is the durable path for this payload.
  void writeApplicationsCacheToIndexedDb(data, timestamp, councilSlug, areaId);
};

const readBestApplicationsCacheEntry = async (councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  let bestEntry = getHotApplicationsCacheEntry(councilSlug, areaId);
  const localEntry = readApplicationsCacheEntry(councilSlug, areaId);
  if (localEntry && (!bestEntry || localEntry.timestamp > bestEntry.timestamp)) {
    bestEntry = localEntry;
  }

  const idbEntry = await readApplicationsCacheEntryFromIndexedDb(councilSlug, areaId);
  if (idbEntry && (!bestEntry || idbEntry.timestamp > bestEntry.timestamp)) {
    bestEntry = idbEntry;
  }

  if (bestEntry) {
    promoteHotApplicationsCacheEntry(bestEntry, councilSlug, areaId);
  }

  return bestEntry;
};

const scheduleIdleTask = (callback, timeoutMs = FORM_INDEX_IDLE_DELAY_MS) => {
  if (typeof window === 'undefined') return () => {};

  let hasRun = false;
  let idleId = null;
  let timerId = null;
  const runOnce = () => {
    if (hasRun) return;
    hasRun = true;
    callback();
  };

  timerId = window.setTimeout(() => {
    timerId = null;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(runOnce);
      return;
    }
    runOnce();
  }, Math.max(0, Number(timeoutMs) || 0));

  return () => {
    if (timerId !== null) {
      window.clearTimeout(timerId);
    }
    if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
  };
};

const fetchFullApplicationsShared = async ({
  retries = 3,
  timeoutMs = 15000,
  bypassCooldown = false,
  onBatch,
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
  preferSnapshot = false,
  snapshotOnly = false,
} = {}) => {
  const areaKey = normalizeAreaScopeId(areaId);
  const councilSlug = normalizeCouncilScope(council);
  const councilKey = getApplicationsScopeKey(councilSlug, areaKey);
  const councilApiParam = getCouncilApiParam(councilSlug);
  const canUseSnapshot = !isAggregateCouncilScope(councilSlug);
  const hotEntry = sharedFullDatasetByCouncil.get(councilKey);
  const now = Date.now();
  if (
    !bypassCooldown &&
    isCacheableApplicationsDataset(hotEntry?.data) &&
    (now - hotEntry.timestamp) < FULL_DATASET_FETCH_COOLDOWN_MS
  ) {
    return hotEntry.data;
  }

  const activeRequest = sharedFullDatasetFetchPromiseByCouncil.get(councilKey);
  if (activeRequest) {
    return activeRequest;
  }

  const request = (async () => {
    let snapshotError = null;

    if (preferSnapshot && PREFER_PLANNING_SNAPSHOT_REFRESH && isAggregateCouncilScope(councilKey)) {
      const publicCouncilSlugs = getPublicCouncilSlugs();
      const snapshotResults = await Promise.all(
        publicCouncilSlugs.map(async (publicCouncilSlug) => {
          try {
            const councilSnapshot = await fetchPlanningMapFeedSnapshot({
              council: publicCouncilSlug,
              areaId: areaKey || undefined,
              retries: Math.min(Number(retries) || 0, 1),
              timeoutMs: Math.min(
                Number(timeoutMs) || FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS,
                FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS
              ),
            });

            if (Array.isArray(councilSnapshot) && councilSnapshot.length > 0) {
              writeApplicationsCache(councilSnapshot, publicCouncilSlug, areaKey);
              if (typeof onBatch === 'function') {
                onBatch(councilSnapshot);
              }
              return councilSnapshot;
            }
          } catch (error) {
            snapshotError = error;
          }

          return [];
        })
      );
      const aggregateSnapshotData = mergeAndSortApplicationCollections(
        snapshotResults.flat(),
        []
      );

      if (aggregateSnapshotData.length > 0) {
        writeApplicationsCache(aggregateSnapshotData, councilSlug, areaKey);
        return aggregateSnapshotData;
      }

      if (snapshotOnly) {
        throw snapshotError || new Error('Aggregate planning media snapshot hydration returned no applications');
      }
    }

    if (preferSnapshot && PREFER_PLANNING_SNAPSHOT_REFRESH && canUseSnapshot) {
      try {
        const snapshotData = await fetchPlanningMapFeedSnapshot({
          council: councilApiParam,
          areaId: areaKey || undefined,
          retries: Math.min(Number(retries) || 0, 1),
          timeoutMs: Math.min(
            Number(timeoutMs) || FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS,
            FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS
          ),
        });

        if (Array.isArray(snapshotData) && snapshotData.length > 0) {
          if (typeof onBatch === 'function') {
            onBatch(snapshotData);
          }

          if (!snapshotData.partial) {
            writeApplicationsCache(snapshotData, councilSlug, areaKey);
            return snapshotData;
          }

          if (snapshotOnly) {
            return snapshotData;
          }
        }
      } catch (error) {
        snapshotError = error;
        // Fall through to paged fetches unless the caller explicitly requested snapshot-only hydration.
      }
    }

    if (snapshotOnly && canUseSnapshot) {
      throw snapshotError || new Error('Planning snapshot hydration returned no applications');
    }

    try {
      const data = await fetchAllUnifiedPlanningFeedLight({
        retries,
        timeoutMs,
        includeShortLets: false,
        pageSize: FULL_DATASET_FETCH_PAGE_SIZE,
        pageConcurrency: FULL_DATASET_FETCH_PAGE_CONCURRENCY,
        maxPages: FULL_DATASET_FETCH_MAX_PAGES,
        onBatch,
        council: councilApiParam,
        areaId: areaKey || undefined,
      });

      if (Array.isArray(data) && data.length > 0) {
        writeApplicationsCache(data, councilSlug, areaKey);
      }

      return data;
    } catch (lightFeedError) {
      try {
        const data = await fetchAllUnifiedPlanningFeed({
          retries,
          timeoutMs,
          includeShortLets: false,
          pageSize: FULL_DATASET_FETCH_PAGE_SIZE,
          pageConcurrency: FULL_DATASET_FETCH_PAGE_CONCURRENCY,
          maxPages: FULL_DATASET_FETCH_MAX_PAGES,
          onBatch,
          council: councilApiParam,
          areaId: areaKey || undefined,
        });

        if (Array.isArray(data) && data.length > 0) {
          writeApplicationsCache(data, councilSlug, areaKey);
        }

        return data;
      } catch (pagedError) {
        if (!PREFER_PLANNING_SNAPSHOT_REFRESH) {
          throw pagedError;
        }

        const snapshotData = await fetchPlanningMapFeedSnapshot({
          council: councilApiParam,
          areaId: areaKey || undefined,
          retries: 0,
          timeoutMs: PLANNING_MAP_SNAPSHOT_FALLBACK_TIMEOUT_MS,
        });

        if (Array.isArray(snapshotData) && snapshotData.length > 0) {
          writeApplicationsCache(snapshotData, councilSlug, areaKey);
          return snapshotData;
        }

        throw pagedError;
      }
    }
  })()
    .finally(() => {
      sharedFullDatasetFetchPromiseByCouncil.delete(councilKey);
    });

  sharedFullDatasetFetchPromiseByCouncil.set(councilKey, request);
  return request;
};

const fetchPlanningSnapshotShared = async ({
  retries = 2,
  timeoutMs = PLANNING_MAP_SNAPSHOT_FALLBACK_TIMEOUT_MS,
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
} = {}) => {
  const data = await fetchPlanningMapFeedSnapshot({
    retries,
    timeoutMs,
    council: getCouncilApiParam(council),
    areaId: normalizeAreaScopeId(areaId) || undefined,
  });
  return Array.isArray(data) ? data : [];
};

const fetchShortLetsShared = async ({
  retries = 3,
  timeoutMs = 15000,
  bypassCooldown = false,
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
} = {}) => {
  const areaKey = normalizeAreaScopeId(areaId);
  const councilSlug = normalizeCouncilScope(council);
  const councilKey = getApplicationsScopeKey(councilSlug, areaKey);
  const councilApiParam = getCouncilApiParam(councilSlug);
  const hotEntry = sharedShortLetsByCouncil.get(councilKey);
  const now = Date.now();
  if (
    !bypassCooldown &&
    Array.isArray(hotEntry?.data) &&
    hotEntry.data.length > 0 &&
    (now - hotEntry.timestamp) < FULL_DATASET_FETCH_COOLDOWN_MS
  ) {
    return hotEntry.data;
  }

  const activeRequest = sharedShortLetsFetchPromiseByCouncil.get(councilKey);
  if (activeRequest) {
    return activeRequest;
  }

  const request = fetchAllShortTermLetsLight({
    retries,
    timeoutMs,
    pageSize: SHORT_LETS_FETCH_PAGE_SIZE,
    pageConcurrency: FULL_DATASET_FETCH_PAGE_CONCURRENCY,
    maxPages: SHORT_LETS_FETCH_MAX_PAGES,
    council: councilApiParam,
    areaId: areaKey || undefined,
  })
    .then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        sharedShortLetsByCouncil.set(councilKey, {
          data,
          timestamp: Date.now(),
        });
      }
      return data;
    })
    .finally(() => {
      sharedShortLetsFetchPromiseByCouncil.delete(councilKey);
    });

  sharedShortLetsFetchPromiseByCouncil.set(councilKey, request);
  return request;
};

const fetchShortLetsSnapshotShared = async ({
  retries = 2,
  timeoutMs = 30000,
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
} = {}) => {
  const data = await fetchShortTermLetsLightSnapshot({
    retries,
    timeoutMs,
    council: getCouncilApiParam(council),
    areaId: normalizeAreaScopeId(areaId) || undefined,
  });
  return Array.isArray(data) ? data : [];
};

// Default planning page ordering should reflect when City Scope first saw a
// record, not the latest scrape pass that refreshed it.
const getMostRelevantDate = (app) => {
  return (
    app?.created_at ||
    app?.scraped_date ||
    app?.validated_date ||
    app?.received_date ||
    null
  );
};

const getCouncilConfigFromApplication = (application) => {
  const rawCouncil =
    application?.source_council ||
    application?.council_slug ||
    application?.council ||
    application?.sourceCouncil;

  const rawCouncilConfig = getCouncilConfig(rawCouncil);
  if (rawCouncilConfig) return rawCouncilConfig;

  const sourceUrl = String(
    application?.link ||
    application?.url ||
    application?.application_url ||
    application?.source_url ||
    ''
  ).toLowerCase();

  if (!sourceUrl) return null;

  return Object.values(COUNCILS).find((council) => {
    const portalBase = String(council.portalBaseUrl || '').toLowerCase();
    if (!portalBase) return false;
    try {
      const portalHost = new URL(portalBase).hostname.toLowerCase();
      return sourceUrl.includes(portalHost);
    } catch (_) {
      return sourceUrl.includes(portalBase);
    }
  }) || null;
};

const getApplicationCouncilSlug = (application, fallbackCouncilSlug = DEFAULT_COUNCIL_SLUG) => {
  const rawCouncil =
    application?.source_council ||
    application?.council_slug ||
    application?.council ||
    application?.sourceCouncil ||
    fallbackCouncilSlug;
  return getCouncilConfigFromApplication(application)?.slug || normalizeCouncilScope(rawCouncil);
};

const isApplicationInPublicCouncil = (application) => {
  const council = getCouncilConfigFromApplication(application);
  return council ? council.publicEnabled === true : true;
};

const filterApplicationsForCouncil = (data = [], councilSlug = DEFAULT_COUNCIL_SLUG) => {
  const councilKey = normalizeCouncilScope(councilSlug);
  if (!Array.isArray(data) || data.length === 0) return [];
  if (councilKey === 'london') return data.filter((app) => isLondonScope(app.source_council));
  if (isAggregateCouncilScope(councilKey)) return data.filter((app) => !isLondonScope(app.source_council) && isApplicationInPublicCouncil(app));
  return data.filter((application) => getApplicationCouncilSlug(application) === councilKey);
};

const buildHotApplicationsSeed = (councilSlug = DEFAULT_COUNCIL_SLUG, currentApplications = []) => {
  const councilKey = normalizeCouncilScope(councilSlug);
  const currentData = Array.isArray(currentApplications) ? currentApplications : [];

  if (!isAggregateCouncilScope(councilKey)) {
    const fromCurrent = filterApplicationsForCouncil(currentData, councilKey);
    if (fromCurrent.length > 0) return fromCurrent;

    const aggregateEntry = getHotApplicationsCacheEntry(AGGREGATE_COUNCIL_SLUG);
    const fromAggregate = filterApplicationsForCouncil(aggregateEntry?.data, councilKey);
    if (fromAggregate.length > 0) return fromAggregate;

    return [];
  }

  const aggregateCurrentData = filterApplicationsForCouncil(currentData, councilKey);
  const hotCouncilData = getPublicCouncilSlugs()
    .flatMap((slug) => getHotApplicationsCacheEntry(slug)?.data || []);

  return mergeAndSortApplicationCollections(aggregateCurrentData, hotCouncilData);
};

const getMostRelevantTimestamp = (app) => {
  const candidate = getMostRelevantDate(app);
  if (!candidate) return 0;
  const parsed = new Date(candidate).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortApplicationsByRecency = (records = []) =>
  [...records].sort((left, right) => {
    const diff = getMostRelevantTimestamp(right) - getMostRelevantTimestamp(left);
    if (diff !== 0) return diff;
    return String(right?.reference_no || right?.ref || '').localeCompare(
      String(left?.reference_no || left?.ref || '')
    );
  });

const getPreferredAddedDate = (app) => {
  const candidate = app?.created_at || app?.scraped_date;
  if (!candidate) return null;

  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return candidate;
};

const getFlexibleAddedDate = (app) => {
  const preferred = getPreferredAddedDate(app);
  if (preferred) return preferred;

  if (app?.scraped_date) {
    const parsed = new Date(app.scraped_date);
    if (!Number.isNaN(parsed.getTime())) {
      return app.scraped_date;
    }
  }

  return null;
};

const normalizeReference = (value) => {
  if (!value) return "";
  return value.toString().trim().toUpperCase();
};

const RANGE_UNIT_PATTERN = /\b(\d{1,2})\s*([-/])\s*(\d{1,2})\s*(storeys?|stories?|floors?)\b/gi;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const repairCollapsedRangeInSummary = (summary, description) => {
  const summaryText = typeof summary === 'string' ? summary.trim() : '';
  const descriptionText = typeof description === 'string' ? description.trim() : '';
  if (!summaryText || !descriptionText) return summary;

  let repairedSummary = summaryText;
  for (const match of descriptionText.matchAll(RANGE_UNIT_PATTERN)) {
    const [, start, separator, end, unit] = match;
    const collapsedNumber = `${start}${end}`;
    const rangeText = `${start}${separator}${end}`;
    const collapsedPattern = new RegExp(`\\b${escapeRegExp(collapsedNumber)}\\s+${escapeRegExp(unit)}\\b`, 'gi');
    repairedSummary = repairedSummary.replace(collapsedPattern, `${rangeText} ${unit}`);
  }

  return repairedSummary;
};

const mergeApplicationCollections = (preferredRecords = [], baseRecords = []) => {
  const recordsByReference = new Map();

  baseRecords.forEach((record, index) => {
    const referenceKey = applicationIdentityKey(record || {}) || normalizeReference(
      record?.reference_no || record?.ref || record?.reference || record?.application_ref
    ) || `BASE_REF_${index}`;
    recordsByReference.set(referenceKey, record);
  });

  preferredRecords.forEach((record, index) => {
    const referenceKey = applicationIdentityKey(record || {}) || normalizeReference(
      record?.reference_no || record?.ref || record?.reference || record?.application_ref
    ) || `PREFERRED_REF_${index}`;
    const existing = recordsByReference.get(referenceKey);
    recordsByReference.set(referenceKey, existing ? { ...existing, ...record } : record);
  });

  return Array.from(recordsByReference.values());
};

const mergeAndSortApplicationCollections = (preferredRecords = [], baseRecords = []) =>
  sortApplicationsByRecency(mergeApplicationCollections(preferredRecords, baseRecords));

const fetchExpectedPlanningApplicationCount = async (councilSlug = DEFAULT_COUNCIL_SLUG, areaId = null) => {
  if (typeof window === 'undefined') return null;
  const councilKey = normalizeCouncilScope(councilSlug);
  const areaKey = normalizeAreaScopeId(areaId);

  try {
    const result = await Promise.race([
      fetchPlanningFeedCounts({ areaId: areaKey || undefined }),
      new Promise((resolve) => {
        window.setTimeout(() => resolve(null), EXPECTED_COUNT_FETCH_TIMEOUT_MS);
      }),
    ]);
    if (!result) return null;
    return getPlanningCountForScope(result.counts, councilKey);
  } catch (_) {
    return null;
  }
};

const isDatasetCloseToExpectedCount = (data, expectedCount) => {
  if (!Array.isArray(data) || data.length === 0) return false;
  const expected = Number(expectedCount);
  if (!Number.isFinite(expected) || expected <= 0) return true;
  return data.length >= Math.max(0, expected - APPLICATIONS_CACHE_COUNT_TOLERANCE);
};

const fetchLatestApplicationsPageShared = async ({
  retries = 1,
  timeoutMs = LATEST_PAGE_REFRESH_TIMEOUT_MS,
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
} = {}) => {
  const areaKey = normalizeAreaScopeId(areaId);
  const councilSlug = normalizeCouncilScope(council);
  const councilKey = getApplicationsScopeKey(councilSlug, areaKey);
  const councilApiParam = getCouncilApiParam(councilSlug);
  const activeRequest = sharedLatestPageRefreshPromiseByCouncil.get(councilKey);
  if (activeRequest) {
    return activeRequest;
  }

  const request = fetchUnifiedPlanningFeedLight({
    retries,
    timeoutMs,
    includeShortLets: false,
    limit: LATEST_PAGE_REFRESH_PAGE_SIZE,
    offset: 0,
    council: councilApiParam,
    areaId: areaKey || undefined,
  })
    .finally(() => {
      sharedLatestPageRefreshPromiseByCouncil.delete(councilKey);
    });

  sharedLatestPageRefreshPromiseByCouncil.set(councilKey, request);
  return request;
};

const refreshCachedApplicationsWithLatestPage = async ({
  cachedData = [],
  seedData = [],
  council = DEFAULT_COUNCIL_SLUG,
  areaId = null,
} = {}) => {
  const areaKey = normalizeAreaScopeId(areaId);
  const councilKey = normalizeCouncilScope(council);
  const baselineData = mergeAndSortApplicationCollections(seedData, cachedData);
  const latestPage = await fetchLatestApplicationsPageShared({ council: councilKey, areaId: areaKey });
  const refreshedData = Array.isArray(latestPage) && latestPage.length > 0
    ? mergeAndSortApplicationCollections(latestPage, baselineData)
    : baselineData;

  if (refreshedData.length > 0) {
    writeApplicationsCache(refreshedData, councilKey, areaKey);
  }

  return refreshedData;
};

const sanitizeApplicationsDataset = (data = []) => {
  const sanitizedData = Array.isArray(data)
    ? data.map((app, index) => {
        const sanitizedApp = sanitizePublicPlanningRecord({ ...app });
        if (sanitizedApp.source_record_only) return sanitizedApp;
        if (sanitizedApp.scale_rating != null) {
          sanitizedApp.scale_rating = Number(sanitizedApp.scale_rating);
        }
        sanitizedApp.reference_no =
          sanitizedApp.reference_no || sanitizedApp.ref || `MISSING_REF_${index}`;
        sanitizedApp.description =
          sanitizedApp.description || sanitizedApp.proposal || sanitizedApp.details || '';
        sanitizedApp.ai_summary = repairCollapsedRangeInSummary(
          sanitizedApp.ai_summary,
          sanitizedApp.description
        );
        sanitizedApp.address = sanitizedApp.address || sanitizedApp.location || 'Unknown Address';

        const documentPreviewUrl = Array.isArray(sanitizedApp.documents)
          ? sanitizedApp.documents.find((document) => {
              if (!document || typeof document !== 'object') return false;
              return [
                document.thumbnail_url,
                document.thumbnail,
                document.preview_url,
                document.preview,
                document.image_url,
              ].some((value) => typeof value === 'string' && value.trim());
            })
          : null;
        const fallbackImageUrl =
          documentPreviewUrl?.thumbnail_url ||
          documentPreviewUrl?.thumbnail ||
          documentPreviewUrl?.preview_url ||
          documentPreviewUrl?.preview ||
          documentPreviewUrl?.image_url ||
          '';
        if (!hasMeaningfulText(sanitizedApp.image_url) && hasMeaningfulText(fallbackImageUrl)) {
          sanitizedApp.image_url = fallbackImageUrl;
        }
        if (!hasMeaningfulText(sanitizedApp.thumbnail_url) && hasMeaningfulText(sanitizedApp.image_url)) {
          sanitizedApp.thumbnail_url = sanitizedApp.image_url;
        }

        if (sanitizedApp.category) {
          sanitizedApp.category = normalizeCategory(sanitizedApp.category);
        }

        return sanitizedApp;
      })
    : [];

  return applyApiOnlyCategorization(sanitizedData).map(app => ({
    ...app,
    category: app.category
  }));
};

const coerceBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "0", "no"].includes(normalized)) return false;
    if (["true", "1", "yes"].includes(normalized)) return true;
  }
  return Boolean(value);
};

// Utility function to check if date is within last 7 days
const isNewThisWeek = (dateString) => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  return date >= sevenDaysAgo;
};

const isApplicationNewThisWeek = (app) => {
  return isNewThisWeek(getFlexibleAddedDate(app));
};

const getDecisionStatusToken = (app) => {
  const sourceValue = [
    app?.status,
    app?.decision,
    app?.decision_status,
    app?.application_status,
    app?.decision_outcome
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  const normalized = sourceValue ? sourceValue.toString().trim().toLowerCase() : '';

  if (!normalized) return 'pending';
  if (normalized.includes('withdrawn')) return 'withdrawn';

  const refusedHints = [
    'application refused',
    'deemed refusal',
    'refused',
    'rejected'
  ];

  if (refusedHints.some((hint) => normalized.includes(hint))) {
    return 'refused';
  }

  const pendingHints = [
    'pending',
    'awaiting',
    'under assessment',
    'in progress',
    'undetermined',
    'not yet determined',
    'consideration',
    'registered',
    'validation'
  ];

  if (pendingHints.some((hint) => normalized.includes(hint))) {
    return 'pending';
  }

  const decidedHints = [
    'decided',
    'approved',
    'granted',
    'allowed',
    'not required',
    'permission',
    'consent',
    'certificate issued',
    'varied',
    'appeal'
  ];

  if (decidedHints.some((hint) => normalized.includes(hint))) {
    return 'decided';
  }

  return 'pending';
};

const normalizeGroupingAddress = (str = '') => (str || '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, '')
  .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, '')
  .replace(/^pf\d+\s*,?\s*/i, '');

const buildGroupedApplicationsCollection = (
  sourceApplications = [],
  { showOnlyGrouped = false } = {}
) => {
  if (!Array.isArray(sourceApplications) || sourceApplications.length === 0) {
    return [];
  }

  const groupsById = new Map();
  const addressGroups = new Map();

  sourceApplications.forEach((app) => {
    if (app.source_record_only) {
      groupsById.set(`source:${applicationIdentityKey(app)}`, [app]);
      return;
    }
    const id = app.group_id != null && app.group_id !== '' ? String(app.group_id) : null;
    if (id) {
      if (!groupsById.has(id)) groupsById.set(id, []);
      groupsById.get(id).push(app);
      return;
    }

    const key = normalizeGroupingAddress(app.address || app.location || '');
    if (!addressGroups.has(key)) addressGroups.set(key, []);
    addressGroups.get(key).push(app);
  });

  const groups = [];

  groupsById.forEach((apps, id) => {
    apps.sort((a, b) => new Date(getMostRelevantDate(b) || 0) - new Date(getMostRelevantDate(a) || 0));
    const firstApp = apps[0];
    const baseAddress = firstApp.address || 'Unknown Address';
    groups.push({
      key: `gid:${id}`,
      baseAddress,
      label: apps.length > 1 ? `${baseAddress} (${apps.length} related)` : baseAddress,
      apps,
      lat: firstApp.latitude ?? firstApp.lat,
      lng: firstApp.longitude ?? firstApp.lng,
    });
  });

  addressGroups.forEach((apps, addrKey) => {
    if (!apps || apps.length === 0) return;
    let target = null;
    for (const group of groups) {
      if (group.apps[0]?.source_record_only) continue;
      const groupKey = normalizeGroupingAddress(group.baseAddress);
      if (groupKey && groupKey === addrKey) {
        target = group;
        break;
      }
    }

    apps.sort((a, b) => new Date(getMostRelevantDate(b) || 0) - new Date(getMostRelevantDate(a) || 0));

    if (target) {
      target.apps.push(...apps);
      target.apps.sort((a, b) => new Date(getMostRelevantDate(b) || 0) - new Date(getMostRelevantDate(a) || 0));
      target.label = target.apps.length > 1 ? `${target.baseAddress} (${target.apps.length} related)` : target.baseAddress;
      return;
    }

    const firstApp = apps[0];
    const baseAddress = firstApp.address || 'Unknown Address';
    groups.push({
      key: `addr:${addrKey}`,
      baseAddress,
      label: apps.length > 1 ? `${baseAddress} (${apps.length} related)` : baseAddress,
      apps,
      lat: firstApp.latitude ?? firstApp.lat,
      lng: firstApp.longitude ?? firstApp.lng,
    });
  });

  groups.sort((a, b) => {
    const dateA = new Date(getMostRelevantDate(a.apps[0]) || 0);
    const dateB = new Date(getMostRelevantDate(b.apps[0]) || 0);
    return dateB.getTime() - dateA.getTime();
  });

  return showOnlyGrouped ? groups.filter((group) => group.apps.length > 1) : groups;
};

const buildVisibleGroupedApplicationsCollection = (
  sourceApplications = [],
  {
    showOnlyGrouped = false,
    visibleGroupCount = INITIAL_VISIBLE_GROUP_COUNT,
  } = {}
) => {
  if (!Array.isArray(sourceApplications) || sourceApplications.length === 0) {
    return [];
  }

  if (showOnlyGrouped || sourceApplications.length <= MIN_GROUPING_SOURCE_APPLICATIONS) {
    return buildGroupedApplicationsCollection(sourceApplications, { showOnlyGrouped });
  }

  const targetVisibleGroups = Math.max(INITIAL_VISIBLE_GROUP_COUNT, Number(visibleGroupCount) || 0);
  const targetGroupCount = targetVisibleGroups + VISIBLE_GROUP_BATCH_SIZE;
  const minSourceLimit = Math.max(
    MIN_GROUPING_SOURCE_APPLICATIONS,
    targetGroupCount * GROUPING_SOURCE_APPLICATIONS_PER_VISIBLE_GROUP
  );

  let sourceLimit = Math.min(sourceApplications.length, minSourceLimit);
  let grouped = buildGroupedApplicationsCollection(
    sourceApplications.slice(0, sourceLimit),
    { showOnlyGrouped }
  );

  while (sourceLimit < sourceApplications.length && grouped.length < targetGroupCount) {
    sourceLimit = Math.min(sourceApplications.length, sourceLimit + GROUPING_SOURCE_GROWTH_STEP);
    grouped = buildGroupedApplicationsCollection(
      sourceApplications.slice(0, sourceLimit),
      { showOnlyGrouped }
    );
  }

  return grouped;
};

export const PlanningContextProvider = ({
  children,
  initialApplications = EMPTY_APPLICATIONS,
  initialLightApplications = EMPTY_APPLICATIONS,
  initialReferenceDate = null,
  disableDataBootstrap = false,
  disableFeedSummary = false,
  suppressLegacyShortLetHydration = false,
  councilSlug = DEFAULT_COUNCIL_SLUG,
  areaId = null,
}) => {
  const router = useRouter();
  const { theme: globalTheme, setTheme: setGlobalTheme } = useTheme();
  const propCouncilSlug = useMemo(() => normalizeCouncilScope(councilSlug), [councilSlug]);
  const propAreaId = useMemo(() => normalizeAreaScopeId(areaId), [areaId]);
  const [runtimeCouncilSlug, setRuntimeCouncilSlug] = useState(propCouncilSlug);
  const [runtimeAreaId, setRuntimeAreaId] = useState(propAreaId);

  useEffect(() => {
    setRuntimeCouncilSlug(propCouncilSlug);
  }, [propCouncilSlug]);
  useEffect(() => {
    setRuntimeAreaId(propAreaId);
  }, [propAreaId]);

  const isAggregateCouncil = isAggregateCouncilScope(runtimeCouncilSlug);
  const councilConfig = useMemo(
    () => (
      isAggregateCouncil
        ? AGGREGATE_COUNCIL_CONFIG
        : runtimeCouncilSlug === 'london' ? LONDON_REGION_CONFIG
        : getCouncilConfig(runtimeCouncilSlug) || getCouncilConfig(DEFAULT_COUNCIL_SLUG)
    ),
    [isAggregateCouncil, runtimeCouncilSlug]
  );
  const currentCouncilSlug = isAggregateCouncil
    ? AGGREGATE_COUNCIL_SLUG
    : councilConfig?.slug || DEFAULT_COUNCIL_SLUG;
  const currentAreaId = runtimeAreaId;
  const usesRemoteSummary = disableDataBootstrap && !disableFeedSummary && !usesRetainedLondonDelivery(currentCouncilSlug);
  const feedSummary = usePlanningFeedSummary(usesRemoteSummary, currentCouncilSlug, currentAreaId);
  const rawSeededApplications = useMemo(() => {
    if (!Array.isArray(initialLightApplications) || initialLightApplications.length === 0) {
      return initialApplications;
    }
    return mergeApplicationCollections(initialApplications, initialLightApplications);
  }, [initialApplications, initialLightApplications]);
  const seededApplications = useMemo(
    () => (
      propCouncilSlug === currentCouncilSlug && propAreaId === currentAreaId
        ? rawSeededApplications
        : []
    ),
    [currentAreaId, currentCouncilSlug, propAreaId, propCouncilSlug, rawSeededApplications]
  );
  const hasSeededFullDataset = propCouncilSlug === currentCouncilSlug
    && propAreaId === currentAreaId
    && Array.isArray(initialLightApplications)
    && initialLightApplications.length > initialApplications.length;
  const stableInitialReferenceDate = useMemo(
    () => normalizeReferenceDateValue(initialReferenceDate) || new Date(),
    [initialReferenceDate]
  );
  const [referenceDate, setReferenceDate] = useState(stableInitialReferenceDate);
  const todayWindowKeyRef = useRef(getLondonTodayWindowKey(stableInitialReferenceDate));
  const [todayWindowGeneration, setTodayWindowGeneration] = useState(0);

  // Animation controls
  const [hasAnimated, setHasAnimated] = useState({
    stats: false,
    tabs: false,
    cards: false,
    filters: false,
    search: false
  });
  
  // Theme and view states
  const [themeMode, setThemeMode] = useState('dark');
  const [viewMode, setViewMode] = useState("grid");
  const viewModeRef = useRef("grid");
  const [newspaperView, setNewspaperView] = useState(false);

  // Data states
  const [applications, setApplications] = useState(seededApplications);
  const applicationsRef = useRef(seededApplications);
  const lastForegroundRefreshAtRef = useRef(Date.now());
  const foregroundRefreshPromiseRef = useRef(null);
  const [shortLetApplications, setShortLetApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(seededApplications.length === 0);
  const [formIndex, setFormIndex] = useState([]);
  const lastScopeKeyRef = useRef(getApplicationsScopeKey(currentCouncilSlug, currentAreaId));
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    applicationsRef.current = applications;
  }, [applications]);

  const readResolvedViewMode = () => (
    readPlanningQueryValue(router.query, 'view') || viewModeRef.current || 'grid'
  );

  const formRefSet = useMemo(() => {
    if (!formIndex || formIndex.length === 0) return new Set();
    const refs = new Set();
    formIndex.forEach((ref) => {
      const normalized = normalizeReference(ref);
      if (normalized) refs.add(normalized);
    });
    return refs;
  }, [formIndex]);
  const formIndexFetchAttemptedRef = useRef(false);
  useEffect(() => {
    formIndexFetchAttemptedRef.current = false;
    setFormIndex([]);
  }, [currentCouncilSlug]);
  const applicationsExposeFormFlag = useMemo(() => {
    if (!Array.isArray(applications) || applications.length === 0) return false;
    return applications.every((app) => typeof app?.has_application_form === 'boolean');
  }, [applications]);
  
  // Track if we're loading the full dataset (beyond initial 500)
  const [_isLoadingFullDataset, setIsLoadingFullDataset] = useState(
    !hasSeededFullDataset && seededApplications.length > 0 && seededApplications.length <= 500
  );

  // UI states
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Create a more robust setSelectedImage function
  const handleSetSelectedImage = (img) => {
    // Force a small delay before setting the image to ensure React has completed its current render cycle
    setTimeout(() => {
      setSelectedImage(img);
    }, 0);
  };
  
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showAiSummaries, setShowAiSummaries] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortByDate, setSortByDate] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("info");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); 
  const [initialScreenCheckDone, setInitialScreenCheckDone] = useState(false);
  const [showRecents, setShowRecents] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [mapView, setMapView] = useState(false);
  const [filterByAddedToday, setFilterByAddedToday] = useState(false);
  const [filterByAddedYesterday, setFilterByAddedYesterday] = useState(false);
  const [filterByScrapedDate, setFilterByScrapedDate] = useState(null);
  const [decisionStatusFilter, setDecisionStatusFilter] = useState('all');
  
  // Scale rating category
  const [selectedScaleCategory, setSelectedScaleCategory] = useState("all");
  const [activeTab, setActiveTab] = useState("applications");

  // Advanced scale filter
  const [advancedScaleMode, setAdvancedScaleMode] = useState(false);
  const [scaleRange, setScaleRange] = useState({ min: 1, max: 10 });

  // Project category filter
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  const [includeSecondaryCategories, setIncludeSecondaryCategories] = useState(false);
  const [multipleCategoryFilters, setMultipleCategoryFilters] = useState([]);
  const [categoryFilterMode, setCategoryFilterMode] = useState('include'); // 'include' or 'exclude'
  const shouldIncludeShortLetsInFeed = useMemo(() => {
    if (categoryFilterMode !== 'include') return false;
    if (Array.isArray(multipleCategoryFilters) && multipleCategoryFilters.includes(SHORT_LETS_CATEGORY_ID)) {
      return true;
    }
    return selectedCategoryFilter === SHORT_LETS_CATEGORY_ID;
  }, [categoryFilterMode, multipleCategoryFilters, selectedCategoryFilter]);
  const effectiveAllApplications = useMemo(() => {
    if (!shouldIncludeShortLetsInFeed || !Array.isArray(shortLetApplications) || shortLetApplications.length === 0) {
      return applications;
    }
    return mergeApplicationCollections(shortLetApplications, applications);
  }, [applications, shortLetApplications, shouldIncludeShortLetsInFeed]);

  const scopedEffectiveAllApplications = useMemo(() => {
    const sourceApplications = Array.isArray(effectiveAllApplications) ? effectiveAllApplications : [];
    if (currentCouncilSlug === AGGREGATE_COUNCIL_SLUG) {
      return sourceApplications.filter(isApplicationInPublicCouncil);
    }

    return sourceApplications.filter((app) => {
      const rawCouncil =
        app?.source_council ||
        app?.council_slug ||
        app?.council ||
        app?.sourceCouncil;
      const appCouncilSlug = getCouncilConfig(rawCouncil)?.slug;

      if (appCouncilSlug) {
        return appCouncilSlug === currentCouncilSlug;
      }

      return currentCouncilSlug === DEFAULT_COUNCIL_SLUG;
    });
  }, [currentCouncilSlug, effectiveAllApplications]);

  const scopedApplicationSearchIndex = useMemo(() => (
    scopedEffectiveAllApplications.map((app) => {
      const addressRefText = [
        app?.address,
        app?.reference_no,
        app?.reference,
        app?.ref,
        app?.application_ref,
      ].filter(Boolean).join(' ').toLowerCase();
      const fullText = [
        getSearchablePlanningApplicationText(app),
        addressRefText,
      ].filter(Boolean).join(' ').toLowerCase();

      return {
        app,
        addressRefText,
        fullText,
      };
    })
  ), [scopedEffectiveAllApplications]);
  
  // Filter to show only grouped applications (2+ apps per group)
  const [showOnlyGroupedApplications, setShowOnlyGroupedApplications] = useState(false);

  // Note: Grouping mode removed - now using backend group_id

  // Navbar visibility – use a ref to avoid rerender-triggering state updates
  const lastScrollYRef = useRef(0);
  const [navbarVisible, setNavbarVisible] = useState(true);

  // Modal state for planning application details
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const modalReturnUrlRef = useRef(null);
  const pendingUrlUpdateRef = useRef(null);
  // Which tab the application modal should open on
  const [initialModalTab, setInitialModalTab] = useState('overview');
  const [initialModalDocumentUrl, setInitialModalDocumentUrl] = useState(null);
  const [initialModalImageExpanded, setInitialModalImageExpanded] = useState(false);

  // State to store original visibility states before modal opens
  const [_originalNavbarVisible, setOriginalNavbarVisible] = useState(true);
  const [originalSidebarOpen, setOriginalSidebarOpen] = useState(true);

  // Client-side categorization is no longer an active background job; backend
  // read models provide categories before the feed reaches the UI.
  const [isCategorizing, setIsCategorizing] = useState(false);
  
  // Two-stream categorization state
  const [categorizedApps, _setCategorizedApps] = useState([]);
  const [uncategorizedApps, _setUncategorizedApps] = useState([]);
  const [backgroundProcessing, setBackgroundProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  // Track when a route change is in progress to avoid fighting navigations
  const [isRouting, setIsRouting] = useState(false);

  const switchCouncil = useCallback((nextCouncilSlug) => {
    const nextSlug = normalizeCouncilScope(nextCouncilSlug);
    setRuntimeCouncilSlug((currentSlug) => (
      currentSlug === nextSlug ? currentSlug : nextSlug
    ));
    setRuntimeAreaId(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncCouncilFromLocation = (event) => {
      const eventHref = event?.detail?.href;
      const eventAreaId = normalizeAreaScopeId(event?.detail?.areaId);
      const path = typeof eventHref === 'string' && eventHref ? eventHref : window.location.pathname;
      const nextSlug = planningCouncilSlugFromPath(
        path
      );
      if (nextSlug) {
        setRuntimeCouncilSlug((currentSlug) => (
          currentSlug === nextSlug ? currentSlug : nextSlug
        ));
      }
      const nextAreaId = eventAreaId || planningAreaIdFromPath(path);
      setRuntimeAreaId((current) => (
        current === nextAreaId ? current : nextAreaId
      ));
    };

    window.addEventListener(PLANNING_REGION_ROUTE_EVENT, syncCouncilFromLocation);
    window.addEventListener('popstate', syncCouncilFromLocation);

    return () => {
      window.removeEventListener(PLANNING_REGION_ROUTE_EVENT, syncCouncilFromLocation);
      window.removeEventListener('popstate', syncCouncilFromLocation);
    };
  }, []);

  useEffect(() => {
    const currentScopeKey = getApplicationsScopeKey(currentCouncilSlug, currentAreaId);
    if (lastScopeKeyRef.current === currentScopeKey) return;
    lastScopeKeyRef.current = currentScopeKey;

    const hotSeedApplications = sanitizeApplicationsDataset(
      currentAreaId ? [] : buildHotApplicationsSeed(currentCouncilSlug, applications)
    );
    const cachedEntry =
      getHotApplicationsCacheEntry(currentCouncilSlug, currentAreaId) ||
      readApplicationsCacheEntry(currentCouncilSlug, currentAreaId);
    const cachedApplications = Array.isArray(cachedEntry?.data) && cachedEntry.data.length > 0
      ? sanitizeApplicationsDataset(cachedEntry.data)
      : hotSeedApplications;

    setApplications(cachedApplications);
    setShortLetApplications([]);
    setFormIndex([]);
    setSelectedApplication(null);
    setIsApplicationModalOpen(false);
    setExpandedIndex(null);
    setVisibleGroupCount(INITIAL_VISIBLE_GROUP_COUNT);
    setProcessingProgress(cachedApplications.length);
    setBackgroundProcessing(false);
    setIsCategorizing(false);
    setIsLoadingFullDataset(cachedApplications.length > 0);
    setIsLoading(cachedApplications.length === 0);
  }, [applications, currentAreaId, currentCouncilSlug]);

  // Debug logging for loading state
  useEffect(() => {
    debugLog(
      'PlanningContext - isCategorizing:',
      isCategorizing,
      'initialApplications.length:',
      initialApplications.length
    );
    debugLog(
      'PlanningContext - categorizedApps:',
      categorizedApps.length,
      'uncategorizedApps:',
      uncategorizedApps.length
    );
  }, [isCategorizing, categorizedApps.length, uncategorizedApps.length, initialApplications.length]);

  useEffect(() => {
    setReferenceDate(stableInitialReferenceDate);
    todayWindowKeyRef.current = getLondonTodayWindowKey(stableInitialReferenceDate);
  }, [stableInitialReferenceDate]);

  useEffect(() => {
    setReferenceDate((currentValue) => {
      const liveNow = new Date();
      if (Math.abs(liveNow.getTime() - currentValue.getTime()) < 1000) {
        return currentValue;
      }
      return liveNow;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const refreshReferenceDate = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      const liveNow = new Date();
      const nextTodayWindowKey = getLondonTodayWindowKey(liveNow);
      if (nextTodayWindowKey === todayWindowKeyRef.current) return;

      todayWindowKeyRef.current = nextTodayWindowKey;
      setReferenceDate(liveNow);
      setTodayWindowGeneration((generation) => generation + 1);
    };

    window.addEventListener('focus', refreshReferenceDate);
    document.addEventListener('visibilitychange', refreshReferenceDate);
    const intervalId = window.setInterval(refreshReferenceDate, 60 * 1000);

    return () => {
      window.removeEventListener('focus', refreshReferenceDate);
      document.removeEventListener('visibilitychange', refreshReferenceDate);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(applications) || applications.length === 0) return;
    if (isLoading || backgroundProcessing || _isLoadingFullDataset) return;
    if (isLondonScope(currentCouncilSlug)) return;
    if (applicationsExposeFormFlag) return;
    if (formIndexFetchAttemptedRef.current) return;

    let cancelled = false;
    const controller = new AbortController();

    const fetchFormIndex = async () => {
      formIndexFetchAttemptedRef.current = true;

      try {
        const refs = new Set();

        const appendRefs = (batch) => {
          if (!Array.isArray(batch)) return;
          batch.forEach((item) => {
            if (!item) return;
            if (typeof item === 'string') {
              const normalized = normalizeReference(item);
              if (normalized) refs.add(normalized);
              return;
            }
            const refValue = item.reference_no || item.reference || item.ref;
            const normalized = normalizeReference(refValue);
            if (normalized) refs.add(normalized);
          });
        };
        const formIndexCouncilParam = isAggregateCouncilScope(currentCouncilSlug)
          ? undefined
          : getCouncilApiParam(currentCouncilSlug);
        const buildFormIndexUrl = (params = {}) => {
          const searchParams = new URLSearchParams(params);
          if (formIndexCouncilParam) {
            searchParams.set('council', formIndexCouncilParam);
          }
          const query = searchParams.toString();
          return `/api/application-forms-index${query ? `?${query}` : ''}`;
        };

        let snapshotLoaded = false;
        if (PREFER_FORM_INDEX_SNAPSHOT) {
          const snapshotRes = await fetch(buildFormIndexUrl(), {
            signal: controller.signal
          });

          if (snapshotRes.ok) {
            const snapshotBatch = await snapshotRes.json();
            appendRefs(snapshotBatch);
            snapshotLoaded = refs.size > 0;
          }
        }

        if (!snapshotLoaded) {
          const limit = 1000;
          let offset = 0;
          let safety = 0;

          while (!cancelled && safety < 10) {
            const res = await fetch(buildFormIndexUrl({ limit: String(limit), offset: String(offset) }), {
              signal: controller.signal
            });
            if (!res.ok) break;
            const batch = await res.json();
            if (!Array.isArray(batch) || batch.length === 0) break;
            appendRefs(batch);
            if (batch.length < limit) break;
            offset += limit;
            safety += 1;
          }
        }

        if (!cancelled && refs.size > 0) {
          setFormIndex(Array.from(refs));
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('Failed to load application forms index', error);
        }
      }
    };

    const cancelScheduledFetch = scheduleIdleTask(() => {
      if (!cancelled) {
        void fetchFormIndex();
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
      cancelScheduledFetch();
    };
  }, [
    _isLoadingFullDataset,
    applications,
    applicationsExposeFormFlag,
    backgroundProcessing,
    currentCouncilSlug,
    isLoading,
  ]);

  useEffect(() => {
    if (!formRefSet || formRefSet.size === 0) return;
    if (!applications || applications.length === 0) return;

    let updated = false;
    const nextApps = applications.map((app) => {
      const existing = coerceBoolean(app?.has_application_form);
      if (existing) return app;
      const refValue = app?.reference_no || app?.ref;
      const normalized = normalizeReference(refValue);
      if (normalized && formRefSet.has(normalized)) {
        updated = true;
        return { ...app, has_application_form: true };
      }
      return app;
    });

    if (updated) {
      setApplications(nextApps);
    }
  }, [formRefSet, applications]);

  // Batch loading state
  const [visibleGroupCount, setVisibleGroupCount] = useState(INITIAL_VISIBLE_GROUP_COUNT); // Show 12 groups initially (4 rows of 3)
  const [hasLoaded] = useState(true); // Set to true by default

  // Moved showToast declaration higher up
  const showToast = useCallback((message, type = "info") => {
    if (DEMO_MODE_ENABLED) return;
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  }, [setNotificationMessage, setNotificationType, setShowNotification]);

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleGroupCount(INITIAL_VISIBLE_GROUP_COUNT);
  }, [selectedFilter, searchQuery, selectedCategoryFilter, selectedScaleCategory, sortByDate, filterByAddedToday, filterByAddedYesterday, filterByScrapedDate, decisionStatusFilter, advancedScaleMode, scaleRange, multipleCategoryFilters, showOnlyGroupedApplications]);

  // Scroll handler – stable reference thanks to useCallback with empty deps
  const handleScroll = useCallback(() => {
    // Don't handle scroll when modal is open
    if (isApplicationModalOpen) return;
    
    const currentScrollY = window.scrollY;

    // Hide navbar when scrolling down, show when scrolling up
    if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
      setNavbarVisible(false);
    } else if (currentScrollY < lastScrollYRef.current) {
      setNavbarVisible(true);
    }

    // Update the ref for next scroll event
    lastScrollYRef.current = currentScrollY;
  }, [isApplicationModalOpen]);

  // Store scroll position for mobile scroll lock
  const scrollPositionRef = useRef(0);

  // Robust scroll lock that works on mobile
  const lockScroll = useCallback(() => {
    if (typeof document === 'undefined') return;

    // Store current scroll position
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;

    // Apply styles to prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';
    document.body.style.left = '0';
    document.body.style.right = '0';

    // Also apply to html element for better mobile support
    document.documentElement.style.overflow = 'hidden';

    // Prevent touch scrolling on mobile
    document.body.style.touchAction = 'none';
    document.body.style.webkitOverflowScrolling = 'auto';
  }, []);

  const clearScrollLockStyles = useCallback(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.touchAction = '';
    document.body.style.webkitOverflowScrolling = '';
    document.documentElement.style.overflow = '';
  }, []);

  const unlockScroll = useCallback(() => {
    if (typeof document === 'undefined') return;

    // Remove scroll lock styles
    clearScrollLockStyles();

    // Restore scroll position
    window.scrollTo(0, scrollPositionRef.current);
  }, [clearScrollLockStyles]);

  // Modal handlers
  const openApplicationModal = useCallback((application, options = {}) => {
    // Always ensure previous scroll locks are cleared before opening a new modal
    clearScrollLockStyles();
    setSelectedApplication(application);
    setIsApplicationModalOpen(true);
    if (options && options.initialTab) {
      setInitialModalTab(options.initialTab);
    } else {
      setInitialModalTab('overview');
    }
    setInitialModalDocumentUrl(
      typeof options?.initialDocumentUrl === 'string' && options.initialDocumentUrl.trim()
        ? options.initialDocumentUrl.trim()
        : null
    );
    setInitialModalImageExpanded(Boolean(options?.initialImageExpanded));
    if (pendingUrlUpdateRef.current) {
      clearTimeout(pendingUrlUpdateRef.current);
      pendingUrlUpdateRef.current = null;
    }

    // Store original states
    setOriginalNavbarVisible(navbarVisible);
    setOriginalSidebarOpen(isSidebarOpen);

    // Hide navbar and sidebar for planning updates page
    setNavbarVisible(false);
    setIsSidebarOpen(false);
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('navbar-visibility', { detail: { visible: false, lock: true } }));
      }
    } catch (_error) {
      void _error;
    }

    // Lock page scrolling with mobile-friendly approach
    lockScroll();

    try {
      if (typeof window !== 'undefined') {
        const reference = application?.reference_no || application?.ref || application?.reference;
        const currentPath = window.location.pathname;

        if (!modalReturnUrlRef.current && currentPath.startsWith('/planning-updates')) {
          modalReturnUrlRef.current = getPlanningUpdatesReturnUrl();
        }

        if (reference) {
          const applicationCouncil =
            application?.source_council ||
            application?.council_slug ||
            application?.council ||
            currentCouncilSlug;
          const nextPath = buildPlanningApplicationPath(reference, applicationCouncil);
          const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
          const isDocumentViewerPath = isPlanningDocumentViewerUrl(currentPathWithSearch);
          if (nextPath && currentPathWithSearch !== nextPath && !isDocumentViewerPath) {
            window.history.pushState(window.history.state, '', nextPath);
          }
        }
      }
    } catch (_error) {
      void _error;
    }
  }, [clearScrollLockStyles, currentCouncilSlug, navbarVisible, isSidebarOpen, lockScroll]);

  const closeApplicationModal = useCallback(() => {
    setIsApplicationModalOpen(false);
    setSelectedApplication(null);
    setInitialModalTab('overview');
    setInitialModalDocumentUrl(null);
    setInitialModalImageExpanded(false);

    // Restore original states - always restore to visible for now
    setNavbarVisible(true);
    setIsSidebarOpen(originalSidebarOpen);
    try {
      if (typeof window !== 'undefined') {
        // Always restore navbar to visible when closing modal
        window.dispatchEvent(new CustomEvent('navbar-visibility', { detail: { visible: true, lock: false } }));
      }
    } catch (_error) {
      void _error;
    }

    // Unlock page scrolling
    unlockScroll();

    try {
      if (
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/planning-application') ||
          Boolean(modalReturnUrlRef.current))
      ) {
        const returnUrl = modalReturnUrlRef.current || '/planning-updates';
        window.history.pushState(window.history.state, '', returnUrl);
      }
    } catch (_error) {
      void _error;
    } finally {
      modalReturnUrlRef.current = null;
    }
  }, [originalSidebarOpen, unlockScroll]);

  // Set animations to played after initial render
  useEffect(() => {
    setHasAnimated({
      stats: true,
      tabs: true,
      cards: true, 
      filters: true,
      search: true
    });
    
    // Ensure scroll is unlocked on page load (cleanup from any previous session)
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.touchAction = '';
      document.body.style.webkitOverflowScrolling = '';
      document.documentElement.style.overflow = '';
    }
  }, []);

  // Add this effect to handle scroll events
  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, 100);
    window.addEventListener('scroll', debouncedScroll, { passive: true });
    return () => window.removeEventListener('scroll', debouncedScroll);
  }, [handleScroll]);

  // Observe route changes so we can avoid URL updates during navigation and
  // ensure any body scroll locks are released when leaving the page
  useEffect(() => {
    const handleRouteStart = (url) => {
      setIsRouting(true);
      try {
        const nextPath = typeof url === 'string' ? url.split('?')[0] : '';
        if (router.pathname === '/planning-updates' && nextPath !== '/planning-updates') {
          // Proactively unlock scroll if navigating away
          unlockScroll();
        }
      } catch (_) { /* no-op */ }
    };
    const handleRouteDone = () => setIsRouting(false);
    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteDone);
    router.events.on('routeChangeError', handleRouteDone);
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteDone);
      router.events.off('routeChangeError', handleRouteDone);
    };
  }, [router, unlockScroll]);

  // Cleanup scroll lock on unmount or when modal state changes
  useEffect(() => {
    return () => {
      // Cleanup scroll lock if component unmounts while modal is open
      if (isApplicationModalOpen) {
        unlockScroll();
      }
    };
  }, [isApplicationModalOpen, unlockScroll]);

  // Check url params and handle direct application reference links
  useEffect(() => {
    if (router.isReady) {
      const nextTheme = router.query.theme;
      if (typeof nextTheme === 'string' && nextTheme !== themeMode) {
        setThemeMode(nextTheme);
      }
      const nextView = router.query.view;
      if (typeof nextView === 'string' && nextView !== viewMode) {
        viewModeRef.current = nextView;
        setViewMode(nextView);
        setNewspaperView(nextView === 'newspaper');
      }
      
      // Enhanced handling of direct application reference links
      let referenceToFind = null;
      
      // Method 1: Check for ref query parameter
      if (router.query.ref) {
        // Ensure we only decode once. router.query.ref should be a string, but we handle arrays just in case.
        const rawRef = Array.isArray(router.query.ref) ? router.query.ref[0] : router.query.ref;
        try {
          referenceToFind = decodeURIComponent(rawRef);
        } catch (_) {
          referenceToFind = rawRef; // Fallback gracefully
        }
      }
      
      // Method 2: Check if we're on /planning-updates and the URL was supposed to be a direct reference
      // This handles cases where redirects might not work properly
      if (!referenceToFind && typeof window !== 'undefined') {
        const pathReference = planningUpdatesReferenceFromPath(window.location.pathname);
        if (pathReference) {
          referenceToFind = pathReference;
          debugLog('PlanningContext: Detected direct URL reference:', referenceToFind);
          // Leave path as-is. Do not replace with ?ref to avoid fighting navigations.
        }
      }
      
      // Method 3: Check for hash-based reference (fallback)
      if (!referenceToFind && typeof window !== 'undefined' && window.location.hash) {
        const hashMatch = window.location.hash.match(/^#ref=(.+)$/);
        if (hashMatch) {
          referenceToFind = decodeURIComponent(hashMatch[1]);
          debugLog('PlanningContext: Detected hash reference:', referenceToFind);
        }
      }
      
      // If we found a reference and have applications loaded, try to open the modal
      if (referenceToFind && effectiveAllApplications.length > 0) {
        debugLog('PlanningContext: Looking for application with reference:', referenceToFind);
        
        const foundApplication = effectiveAllApplications.find(app => {
          // Try multiple reference field variations
          const refs = [
            app.reference_no,
            app.ref,
            app.reference,
            app.application_ref,
            app.app_ref
          ].filter(Boolean);
          
          return refs.some(ref => 
            ref === referenceToFind || 
            ref.toString().toLowerCase() === referenceToFind.toLowerCase()
          );
        });
        
        if (foundApplication) {
          const selectedRefs = selectedApplication ? [
            selectedApplication.reference_no,
            selectedApplication.ref,
            selectedApplication.reference,
            selectedApplication.application_ref,
            selectedApplication.app_ref
          ].filter(Boolean) : [];
          const referenceAlreadyOpen = isApplicationModalOpen && selectedRefs.some(ref =>
            ref === referenceToFind ||
            ref.toString().toLowerCase() === referenceToFind.toLowerCase()
          );
          if (referenceAlreadyOpen) {
            return;
          }

          debugLog('PlanningContext: Found application, opening modal:', foundApplication.reference_no);
          openApplicationModal(foundApplication);
        } else {
          debugWarn('PlanningContext: Application not found for reference:', referenceToFind);
          
          // Show a toast notification if application not found
          showToast(`Planning application "${referenceToFind}" not found. It may have been removed or the reference is incorrect.`, "warning");
        }
      }
    }
  }, [router, router.isReady, router.query, themeMode, viewMode, effectiveAllApplications, selectedApplication, isApplicationModalOpen, openApplicationModal, showToast]);

  // Keep date-slice filters in sync when Next hydrates route query params after the initial data load.
  useEffect(() => {
    if (!router.isReady) return;

    const {
      addedParam,
      normalizedDateParam,
      todayDateString,
      yesterdayDateString,
    } = getDateSliceUrlParams(router.query);

    if (!addedParam && !normalizedDateParam) return;

    setFilterByAddedToday(false);
    setFilterByAddedYesterday(false);
    setFilterByScrapedDate(null);

    if (addedParam === 'today') {
      setFilterByAddedToday(todayDateString);
    } else if (addedParam === 'yesterday') {
      setFilterByAddedYesterday(yesterdayDateString);
    } else if (normalizedDateParam) {
      setFilterByScrapedDate(normalizedDateParam);
    }
  }, [router.isReady, router.query]);

  // Data loading effect
  useEffect(() => {
    const loadGeneration = loadGenerationRef.current + 1;
    loadGenerationRef.current = loadGeneration;
    let cancelled = false;
    const isCurrentLoad = () => !cancelled && loadGenerationRef.current === loadGeneration;
    const cleanupLoad = () => {
      cancelled = true;
    };

    const applyInitialUrlParams = () => {
      const filter = readPlanningQueryValue(router.query, 'filter') || 'all';
      const { rawSearch, search } = readPlanningSearchIntentQueryValues(router.query, currentCouncilSlug);
      const scale = readPlanningQueryValue(router.query, 'scale') || 'all';
      const sort = readPlanningQueryValue(router.query, 'sort') || 'true';
      const view = readResolvedViewMode();
      const categoryFilters = readPlanningCategoryQueryValues(router.query, rawSearch);
      const category = categoryFilters[0] || null;
      const {
        addedParam,
        normalizedDateParam,
        todayDateString,
        yesterdayDateString,
      } = getDateSliceUrlParams(router.query);
      setSelectedFilter(filter);
      setSearchQuery(search);
      setSelectedScaleCategory(scale);
      setSelectedCategoryFilter(category || null);
      setMultipleCategoryFilters(categoryFilters);
      setSortByDate(sort === 'true');
      setViewMode(view);
      setFilterByAddedToday(false);
      setFilterByAddedYesterday(false);
      setFilterByScrapedDate(null);
      if (addedParam === 'today') {
        setFilterByAddedToday(todayDateString);
      } else if (addedParam === 'yesterday') {
        setFilterByAddedYesterday(yesterdayDateString);
      } else if (normalizedDateParam) {
        setFilterByScrapedDate(normalizedDateParam);
      }
      if (search && search.trim() !== '') {
        setRecentSearches((prev) => {
          const filtered = prev.filter((item) => item !== search);
          return [search, ...filtered].slice(0, 5);
        });
      }
      setInitialScreenCheckDone(true);
    };

    const runtimeSeededApplications = currentAreaId
      ? []
      : sanitizeApplicationsDataset(buildHotApplicationsSeed(currentCouncilSlug, applications));
    const effectiveSeededApplications =
      runtimeSeededApplications.length > seededApplications.length
        ? runtimeSeededApplications
        : seededApplications;
    const hasSeededAllPublicCouncils = !isAggregateCouncilScope(currentCouncilSlug)
      || getPublicCouncilSlugs().every((slug) => (
        effectiveSeededApplications.some((app) => getApplicationCouncilSlug(app) === slug)
      ));
    const effectiveHasSeededFullDataset =
      hasSeededFullDataset || (effectiveSeededApplications.length > 500 && hasSeededAllPublicCouncils);

    // If initial data was injected via props, hydrate state quickly and fetch in background.
    if (effectiveSeededApplications && effectiveSeededApplications.length) {
      const shouldBootstrapFullDataset =
        !disableDataBootstrap &&
        !effectiveHasSeededFullDataset &&
        (
          effectiveSeededApplications.length <= 500 ||
          isAggregateCouncilScope(currentCouncilSlug)
        );

      setIsCategorizing(false);
      setIsLoadingFullDataset(false);

      const sanitizeAndSet = (data) => {
        if (!isCurrentLoad()) return;
        const allApps = sanitizeApplicationsDataset(data);
        debugLog('📊 API categorization applied to:', allApps.length, 'applications');
        const shouldTransition = allApps.length > 500 && applications.length > 0;
        if (shouldTransition && typeof React.startTransition === 'function') {
          React.startTransition(() => setApplications(allApps));
        } else {
          setApplications(allApps);
        }
        setIsLoading(false);
        
        // No background processing needed - all categorization is instant
      };

      const createProgressiveHydrator = (seedData = []) => {
        const recordsByReference = new Map();
        let lastAppliedCount = 0;
        let lastAppliedAt = 0;

        const mergeRecords = (records = []) => {
          if (!Array.isArray(records) || records.length === 0) return false;
          let changed = false;

          records.forEach((record, index) => {
            const referenceKey = applicationIdentityKey(record || {}) || normalizeReference(
              record?.reference_no || record?.ref || record?.reference || record?.application_ref
            ) || `BATCH_REF_${recordsByReference.size}_${index}`;
            const existing = recordsByReference.get(referenceKey);
            if (!existing) {
              recordsByReference.set(referenceKey, record);
              changed = true;
              return;
            }

            if (existing === record) {
              return;
            }

            const nextRecord = { ...existing, ...record };
            recordsByReference.set(referenceKey, nextRecord);
            if (existing !== nextRecord) {
              changed = true;
            }
          });

          return changed;
        };

        const applyMergedRecords = (force = false) => {
          if (!isCurrentLoad()) return;
          const mergedRecords = Array.from(recordsByReference.values());
          if (mergedRecords.length === 0) return;

          const now = Date.now();
          const countDelta = mergedRecords.length - lastAppliedCount;
          if (
            !force &&
            countDelta < BACKGROUND_DATA_APPLY_MIN_DELTA &&
            (now - lastAppliedAt) < BACKGROUND_DATA_APPLY_INTERVAL_MS
          ) {
            return;
          }

          lastAppliedCount = mergedRecords.length;
          lastAppliedAt = now;
          setBackgroundProcessing(true);
          setProcessingProgress(mergedRecords.length);
          sanitizeAndSet(mergedRecords);
        };

        mergeRecords(seedData);
        if (Array.isArray(seedData) && seedData.length > 0) {
          lastAppliedCount = seedData.length;
          setProcessingProgress(seedData.length);
        }

        return {
          onBatch: (batch) => {
            const changed = mergeRecords(batch);
            if (changed) {
              applyMergedRecords(false);
            }
          },
          finalize: (records) => {
            mergeRecords(records);
            applyMergedRecords(true);
          }
        };
      };

      // Process the initial, partial data without affecting the loading indicator.
      sanitizeAndSet(effectiveSeededApplications);
      setProcessingProgress(effectiveSeededApplications.length);
      setBackgroundProcessing(false);

      // Apply URL parameters once
      applyInitialUrlParams();

      if (effectiveHasSeededFullDataset) {
        writeApplicationsCache(effectiveSeededApplications, currentCouncilSlug, currentAreaId);
      }

      if (!shouldBootstrapFullDataset) {
        return cleanupLoad;
      }

      // The server-rendered slice is usable. Loading state begins only when a
      // full-dataset request actually starts, not during the intent delay.

      const shouldHydrateFullDatasetForCurrentRoute = () => {
        const filter = readPlanningQueryValue(router.query, 'filter') || 'all';
        const { rawSearch } = readPlanningSearchIntentQueryValues(router.query, currentCouncilSlug);
        const scale = readPlanningQueryValue(router.query, 'scale') || 'all';
        const view = readResolvedViewMode();
        const category = readPlanningCategoryQueryValues(router.query, rawSearch)[0] || null;
        const { addedParam, normalizedDateParam } = getDateSliceUrlParams(router.query);

        return Boolean(
          category ||
          addedParam ||
          normalizedDateParam ||
          filter !== 'all' ||
          scale !== 'all' ||
          view !== 'grid'
        );
      };

      const runBackgroundHydration = async () => {
        try {
          const cachedEntry = await readBestApplicationsCacheEntry(currentCouncilSlug, currentAreaId);
          if (!isCurrentLoad()) return;
          const cachedData = Array.isArray(cachedEntry?.data) ? cachedEntry.data : null;
          const hasMoreCompleteCachedData =
            Array.isArray(cachedData) && cachedData.length > effectiveSeededApplications.length;
          const mergedCachedData = hasMoreCompleteCachedData
            ? mergeAndSortApplicationCollections(effectiveSeededApplications, cachedData)
            : null;
          const expectedApplicationCount = hasMoreCompleteCachedData
            ? await fetchExpectedPlanningApplicationCount(currentCouncilSlug, currentAreaId)
            : null;
          if (!isCurrentLoad()) return;
          const cachedDataMatchesExpected =
            hasMoreCompleteCachedData &&
            isDatasetCloseToExpectedCount(mergedCachedData, expectedApplicationCount);
          const cacheIsFresh =
            hasMoreCompleteCachedData &&
            cachedDataMatchesExpected &&
            (Date.now() - cachedEntry.timestamp) <= APPLICATIONS_REFRESH_SKIP_MS;
          const progressiveHydrator = createProgressiveHydrator(
            mergedCachedData || effectiveSeededApplications
          );

          if (hasMoreCompleteCachedData) {
            debugLog(
              'PlanningContext: Hydrating from cached dataset before latest-page refresh, length:',
              mergedCachedData.length
            );
            sanitizeAndSet(mergedCachedData);
            setProcessingProgress(mergedCachedData.length);
            setBackgroundProcessing(false);
            setIsCategorizing(false);
            setIsLoadingFullDataset(false);
          }

          if (cacheIsFresh) {
            return; // Fresh full cache already applied; no need to refetch on mount.
          }

          if (hasMoreCompleteCachedData) {
            let refreshedData = null;
            try {
              refreshedData = await refreshCachedApplicationsWithLatestPage({
                cachedData: mergedCachedData,
                council: currentCouncilSlug,
                areaId: currentAreaId,
              });
              if (!isCurrentLoad()) return;
              if (Array.isArray(refreshedData) && refreshedData.length > 0) {
                sanitizeAndSet(refreshedData);
                setProcessingProgress(refreshedData.length);
              }
          } catch (latestPageError) {
              debugWarn(
                'PlanningContext: Latest-page cache refresh failed',
                summarizeRecoverableError(latestPageError)
              );
            }
            const refreshedDataMatchesExpected = isDatasetCloseToExpectedCount(
              refreshedData || mergedCachedData,
              expectedApplicationCount
            );
            if (!expectedApplicationCount || refreshedDataMatchesExpected) {
              return;
            }
          }

          // Background refresh to fetch the complete dataset (deduped across mounts).
          setBackgroundProcessing(true);
          setIsLoadingFullDataset(true);
          let freshData = [];
          let fastIndexPromise = null;
          if (!isAggregateCouncilScope(currentCouncilSlug)) {
            fastIndexPromise = fetchAllUnifiedPlanningFeedLight({
              retries: 0,
              timeoutMs: FAST_COUNCIL_INDEX_TIMEOUT_MS,
              includeShortLets: false,
              pageSize: FULL_DATASET_FETCH_PAGE_SIZE,
              pageConcurrency: FULL_DATASET_FETCH_PAGE_CONCURRENCY,
              maxPages: FULL_DATASET_FETCH_MAX_PAGES,
              onBatch: progressiveHydrator.onBatch,
              council: currentCouncilSlug,
              areaId: currentAreaId || undefined,
            }).catch((error) => {
              debugWarn('PlanningContext: Fast council index hydration failed', summarizeRecoverableError(error));
              return [];
            });
          }
          try {
            freshData = await fetchFullApplicationsShared({
              retries: 0,
              timeoutMs: FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS,
              bypassCooldown: true,
              onBatch: progressiveHydrator.onBatch,
              council: currentCouncilSlug,
              areaId: currentAreaId,
              preferSnapshot: false,
              snapshotOnly: false,
            });
            if (!isCurrentLoad()) return;
          } catch (_) {
            freshData = isAggregateCouncilScope(currentCouncilSlug)
              ? (currentAreaId ? [] : buildHotApplicationsSeed(currentCouncilSlug, applications))
              : await fetchPlanningSnapshotShared({
                  retries: 0,
                  timeoutMs: PLANNING_MAP_SNAPSHOT_FALLBACK_TIMEOUT_MS,
                  council: currentCouncilSlug,
                  areaId: currentAreaId,
                });
            if (!isCurrentLoad()) return;
          }
          if (fastIndexPromise) {
            const fastIndexData = await fastIndexPromise;
            if (!isCurrentLoad()) return;
            if (
              Array.isArray(fastIndexData) &&
              fastIndexData.length > freshData.length
            ) {
              progressiveHydrator.finalize(fastIndexData);
            }
          }
          if (freshData && freshData.length) {
            // Process the complete dataset
            progressiveHydrator.finalize(freshData);
          }
        } catch (e) {
          console.warn('Background refresh failed', summarizeRecoverableError(e));
        } finally {
          // Turn off loading indicator even if fetch fails
          if (isCurrentLoad()) {
            setBackgroundProcessing(false);
            setIsLoadingFullDataset(false);
          }
        }
      };

      const startHydration = () => {
        void runBackgroundHydration();
      };

      if (shouldHydrateFullDatasetForCurrentRoute()) {
        const cancelScheduledHydration = scheduleIdleTask(startHydration, 3500);
        return () => {
          cleanupLoad();
          if (typeof cancelScheduledHydration === 'function') {
            cancelScheduledHydration();
          }
        };
      }

      if (typeof window === 'undefined') {
        return cleanupLoad;
      }

      let hasStarted = false;
      const startOnUserIntent = () => {
        if (hasStarted || !isCurrentLoad()) return;
        hasStarted = true;
        cleanupIntentListeners();
        startHydration();
      };
      const cleanupIntentListeners = () => {
        window.removeEventListener(FULL_DATASET_INTENT_EVENT, startOnUserIntent);
      };

      window.addEventListener(FULL_DATASET_INTENT_EVENT, startOnUserIntent);

      return () => {
        cleanupLoad();
        cleanupIntentListeners();
      };
    }

    // This section handles loading from cache if no initial props are passed.
    const getCachedAppsEntry = async () => readBestApplicationsCacheEntry(currentCouncilSlug, currentAreaId);

    const saveCache = (data) => {
      writeApplicationsCache(data, currentCouncilSlug, currentAreaId);
    };

    const loadData = async (silently = false, { bypassCooldown = false, onBatch } = {}) => {
      if (!silently) {
        setIsLoading(true);
      }
      try {
        let data = [];
        try {
          data = await fetchFullApplicationsShared({
            retries: 0,
            timeoutMs: FULL_DATASET_RICH_SNAPSHOT_TIMEOUT_MS,
            bypassCooldown: bypassCooldown || isAggregateCouncilScope(currentCouncilSlug),
            onBatch,
            council: currentCouncilSlug,
            areaId: currentAreaId,
            preferSnapshot: true,
            snapshotOnly: true,
          });
          if (!isCurrentLoad()) return [];
        } catch (_) {
          data = isAggregateCouncilScope(currentCouncilSlug)
            ? (currentAreaId ? [] : buildHotApplicationsSeed(currentCouncilSlug, applications))
            : await fetchPlanningSnapshotShared({
                retries: 0,
                timeoutMs: PLANNING_MAP_SNAPSHOT_FALLBACK_TIMEOUT_MS,
                council: currentCouncilSlug,
                areaId: currentAreaId,
              });
          if (!isCurrentLoad()) return [];
        }
        saveCache(data);
        return data;
      } catch (e) {
        console.warn('Planning data load failed', summarizeRecoverableError(e));
        return [];
      } finally {
        if (!silently) {
          if (isCurrentLoad()) {
            setIsLoading(false);
          }
        }
      }
    };

    const sanitizeAndSet = (data) => {
      if (!isCurrentLoad()) return;
      const allApps = sanitizeApplicationsDataset(data);
      debugLog('📊 Regular load - API categorization applied to:', allApps.length, 'applications');
      const shouldTransition = allApps.length > 500 && applications.length > 0;
      if (shouldTransition && typeof React.startTransition === 'function') {
        React.startTransition(() => setApplications(allApps));
      } else {
        setApplications(allApps);
      }

      // Note: Cache clearing removed - no longer using frontend grouping caches

      // Apply URL params
      const filter = readPlanningQueryValue(router.query, 'filter') || 'all';
      const { rawSearch, search } = readPlanningSearchIntentQueryValues(router.query, currentCouncilSlug);
      const scale = readPlanningQueryValue(router.query, 'scale') || 'all';
      const sort = readPlanningQueryValue(router.query, 'sort') || 'true';
      const view = readResolvedViewMode();
      const categoryFilters = readPlanningCategoryQueryValues(router.query, rawSearch);
      const category = categoryFilters[0] || null;
      const {
        addedParam,
        normalizedDateParam,
        todayDateString,
        yesterdayDateString,
      } = getDateSliceUrlParams(router.query);

      setSelectedFilter(filter);
      setSearchQuery(search);
      setSelectedScaleCategory(scale);
      setSelectedCategoryFilter(category || null);
      setMultipleCategoryFilters(categoryFilters);
      setSortByDate(sort === 'true');
      setViewMode(view);
      setFilterByAddedToday(false);
      setFilterByAddedYesterday(false);
      setFilterByScrapedDate(null);
      if (addedParam === 'today') {
        setFilterByAddedToday(todayDateString);
      } else if (addedParam === 'yesterday') {
        setFilterByAddedYesterday(yesterdayDateString);
      } else if (normalizedDateParam) {
        setFilterByScrapedDate(normalizedDateParam);
      }

      if (search && search.trim() !== '') {
        setRecentSearches((prev) => {
          const filtered = prev.filter((item) => item !== search);
          return [search, ...filtered].slice(0, 5);
        });
      }

      setInitialScreenCheckDone(true);
      
      // No background processing needed - all categorization is instant
    };

    const createRuntimeProgressiveHydrator = (seedData = []) => {
      const recordsByReference = new Map();
      let lastAppliedCount = 0;
      let lastAppliedAt = 0;

      const mergeRecords = (records = []) => {
        if (!Array.isArray(records) || records.length === 0) return false;
        let changed = false;

        records.forEach((record, index) => {
          const referenceKey = applicationIdentityKey(record || {}) || normalizeReference(
            record?.reference_no || record?.ref || record?.reference || record?.application_ref
          ) || `RUNTIME_BATCH_REF_${recordsByReference.size}_${index}`;
          const existing = recordsByReference.get(referenceKey);
          if (!existing) {
            recordsByReference.set(referenceKey, record);
            changed = true;
            return;
          }

          const nextRecord = { ...existing, ...record };
          recordsByReference.set(referenceKey, nextRecord);
          if (existing !== nextRecord) {
            changed = true;
          }
        });

        return changed;
      };

      const applyMergedRecords = (force = false) => {
        if (!isCurrentLoad()) return;
        const mergedRecords = Array.from(recordsByReference.values());
        if (mergedRecords.length === 0) return;

        const now = Date.now();
        const countDelta = mergedRecords.length - lastAppliedCount;
        if (
          !force &&
          countDelta < BACKGROUND_DATA_APPLY_MIN_DELTA &&
          (now - lastAppliedAt) < BACKGROUND_DATA_APPLY_INTERVAL_MS
        ) {
          return;
        }

        lastAppliedCount = mergedRecords.length;
        lastAppliedAt = now;
        setBackgroundProcessing(true);
        setProcessingProgress(mergedRecords.length);
        sanitizeAndSet(mergedRecords);
      };

      mergeRecords(seedData);
      if (Array.isArray(seedData) && seedData.length > 0) {
        lastAppliedCount = seedData.length;
        setProcessingProgress(seedData.length);
      }

      return {
        onBatch: (batch) => {
          const changed = mergeRecords(batch);
          if (changed) {
            applyMergedRecords(false);
          }
        },
        finalize: (records) => {
          mergeRecords(records);
          applyMergedRecords(true);
        }
      };
    };

    if (disableDataBootstrap) {
      applyInitialUrlParams();
      setIsLoading(false);
      setIsCategorizing(false);
      setIsLoadingFullDataset(false);
      return cleanupLoad;
    }

    const init = async () => {
      debugLog('PlanningContext - init called, initialApplications.length:', initialApplications.length);
      const cachedEntry = await getCachedAppsEntry();
      if (!isCurrentLoad()) return;
      const cached = Array.isArray(cachedEntry?.data) ? cachedEntry.data : null;
      debugLog('PlanningContext - cached data length:', cached ? cached.length : 0);
      
      if (cached && cached.length > seededApplications.length) {
        const expectedApplicationCount = await fetchExpectedPlanningApplicationCount(currentCouncilSlug, currentAreaId);
        if (!isCurrentLoad()) return;
        const cachedDataMatchesExpected = isDatasetCloseToExpectedCount(
          cached,
          expectedApplicationCount
        );
        debugLog('PlanningContext - using cached data, hiding loading indicator');
        // We have a more complete cached dataset than initial props
        sanitizeAndSet(cached);
        setIsLoading(false);
        setIsCategorizing(false);
        setIsLoadingFullDataset(false);

        const cacheIsFresh =
          cachedDataMatchesExpected &&
          (Date.now() - cachedEntry.timestamp) <= APPLICATIONS_REFRESH_SKIP_MS;
        let refreshedData = null;
        if (!cacheIsFresh) {
          // Refresh only the newest page; the cached full dataset is already the baseline.
          try {
            refreshedData = await refreshCachedApplicationsWithLatestPage({
              cachedData: cached,
              council: currentCouncilSlug,
              areaId: currentAreaId,
            });
            if (!isCurrentLoad()) return;
            if (refreshedData && refreshedData.length > 0) {
              sanitizeAndSet(refreshedData);
            }
          } catch (error) {
            console.warn('Background refresh failed', summarizeRecoverableError(error));
          }
        }
        const refreshedDataMatchesExpected = isDatasetCloseToExpectedCount(
          refreshedData || cached,
          expectedApplicationCount
        );
        if (!expectedApplicationCount || refreshedDataMatchesExpected) {
          return;
        }
      }

      if (!hasSeededFullDataset && seededApplications.length > 0 && seededApplications.length <= 500) {
        debugLog('PlanningContext - loading full dataset, showing loading indicator');
        // We have initial data but need to load the full dataset
        setIsLoadingFullDataset(true);
        
        try {
          const fresh = await loadData(true, { bypassCooldown: true });
          if (!isCurrentLoad()) return;
          debugLog('PlanningContext - loaded fresh data, length:', fresh ? fresh.length : 0);
          if (fresh && fresh.length > seededApplications.length) {
            sanitizeAndSet(fresh);
          }
        } catch (error) {
          console.warn('Failed to load full dataset', summarizeRecoverableError(error));
        } finally {
          debugLog('PlanningContext - finished loading, hiding loading indicator');
          if (isCurrentLoad()) {
            setIsLoadingFullDataset(false);
          }
        }
      } else if (seededApplications.length === 0) {
        debugLog('PlanningContext - no initial data, loading fresh');
        const runtimeSeed = currentAreaId ? [] : buildHotApplicationsSeed(currentCouncilSlug, applications);
        const runtimeHydrator = createRuntimeProgressiveHydrator(runtimeSeed);
        const hasRuntimeSeed = Array.isArray(runtimeSeed) && runtimeSeed.length > 0;

        if (hasRuntimeSeed) {
          sanitizeAndSet(runtimeSeed);
          setProcessingProgress(runtimeSeed.length);
          setIsLoading(false);
          setIsLoadingFullDataset(true);
          setBackgroundProcessing(true);
        }

        // No initial data from this route; keep any runtime seed on screen and
        // merge rich batches as they arrive instead of waiting for the whole
        // region fetch to finish.
        try {
          const fresh = await loadData(hasRuntimeSeed, {
            bypassCooldown: true,
            onBatch: runtimeHydrator.onBatch,
          });
          if (!isCurrentLoad()) return;
          if (fresh && fresh.length > 0) {
            runtimeHydrator.finalize(fresh);
          }
        } catch (error) {
          if (isCurrentLoad()) {
            console.warn('Failed to load initial planning feed', summarizeRecoverableError(error));
          }
        } finally {
          if (isCurrentLoad()) {
            setIsLoadingFullDataset(false);
            setBackgroundProcessing(false);
          }
        }
      }
    };

    init();

    return cleanupLoad;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAreaId,
    currentCouncilSlug,
    disableDataBootstrap,
    hasSeededFullDataset,
    initialApplications.length,
    seededApplications,
    todayWindowGeneration,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined' || disableDataBootstrap) return undefined;

    let cancelled = false;
    lastForegroundRefreshAtRef.current = Date.now();

    const refreshLatestVisibleApplications = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      const now = Date.now();
      if (now - lastForegroundRefreshAtRef.current < APPLICATIONS_REFRESH_SKIP_MS) return;
      if (foregroundRefreshPromiseRef.current) return;

      // Throttle attempts as well as successes so a transient failure cannot create
      // a focus/visibility request loop. The next foreground interaction retries later.
      lastForegroundRefreshAtRef.current = now;
      const baseline = Array.isArray(applicationsRef.current)
        ? applicationsRef.current
        : [];
      const request = refreshCachedApplicationsWithLatestPage({
        cachedData: baseline,
        council: currentCouncilSlug,
        areaId: currentAreaId,
      });
      foregroundRefreshPromiseRef.current = request;

      try {
        const refreshedData = await request;
        if (cancelled || !Array.isArray(refreshedData) || refreshedData.length === 0) return;

        const sanitized = sanitizeApplicationsDataset(refreshedData);
        applicationsRef.current = sanitized;
        setApplications(sanitized);
        setProcessingProgress(sanitized.length);
        setReferenceDate(new Date());
      } catch (error) {
        debugWarn(
          'PlanningContext: Foreground latest-page refresh failed',
          summarizeRecoverableError(error)
        );
      } finally {
        if (foregroundRefreshPromiseRef.current === request) {
          foregroundRefreshPromiseRef.current = null;
        }
      }
    };

    const refreshOnVisibility = () => {
      if (document.visibilityState !== 'hidden') {
        void refreshLatestVisibleApplications();
      }
    };

    const refreshOnFocus = () => {
      void refreshLatestVisibleApplications();
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);
    window.addEventListener('pointerdown', refreshOnFocus, { passive: true });
    window.addEventListener('keydown', refreshOnFocus, { passive: true });
    window.addEventListener('online', refreshOnFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
      window.removeEventListener('pointerdown', refreshOnFocus);
      window.removeEventListener('keydown', refreshOnFocus);
      window.removeEventListener('online', refreshOnFocus);
    };
  }, [currentAreaId, currentCouncilSlug, disableDataBootstrap]);

  useEffect(() => {
    if (typeof window === 'undefined' || disableDataBootstrap) return;
    const needsLegacyDataset = Boolean(
      selectedFilter !== 'all'
      || showOnlyGroupedApplications
    );
    if (needsLegacyDataset) {
      window.dispatchEvent(new Event(FULL_DATASET_INTENT_EVENT));
    }
  }, [
    disableDataBootstrap,
    selectedFilter,
    showOnlyGroupedApplications,
  ]);

  useEffect(() => {
    if (suppressLegacyShortLetHydration || isLondonScope(currentCouncilSlug)) return;
    if (!shouldIncludeShortLetsInFeed) return;
    if (Array.isArray(shortLetApplications) && shortLetApplications.length > 0) return;

    let cancelled = false;
    setBackgroundProcessing(true);

    fetchShortLetsSnapshotShared({
      retries: 2,
      timeoutMs: 30000,
      council: currentCouncilSlug,
      areaId: currentAreaId,
    })
      .catch(() => fetchShortLetsShared({
        retries: 3,
        timeoutMs: 15000,
        council: currentCouncilSlug,
        areaId: currentAreaId,
      }))
      .then((data) => {
        if (cancelled) return;
        const sanitizedShortLets = sanitizeApplicationsDataset(data);
        setShortLetApplications(sanitizedShortLets);
        setProcessingProgress((prev) => {
          const baseCount = Array.isArray(applications) ? applications.length : 0;
          return Math.max(Number(prev) || 0, baseCount + sanitizedShortLets.length);
        });
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Failed to load short-let feed on demand', summarizeRecoverableError(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBackgroundProcessing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    applications,
    currentAreaId,
    currentCouncilSlug,
    shortLetApplications,
    shouldIncludeShortLetsInFeed,
    suppressLegacyShortLetHydration,
  ]);

  // Note: Cache management removed - no longer using frontend grouping caches

  // Auto-hide sidebar when viewport is reduced; keep user's manual open/close on wide screens.
  useEffect(() => {
    const SIDEBAR_AUTO_HIDE_BREAKPOINT = 1280;

    const handleResize = () => {
      const hasRoomForSidebar = window.innerWidth >= SIDEBAR_AUTO_HIDE_BREAKPOINT;
      setIsSidebarOpen((prev) => (hasRoomForSidebar ? prev : false));
      setInitialScreenCheckDone(true);
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (viewMode === 'newspaper') {
      setNewspaperView(true);
    } else if (viewMode === 'map') {
      setMapView(true);
    } else {
      setNewspaperView(false);
      setMapView(false);
    }
  }, [viewMode]);

  const cycleThemeMode = () => {
    setThemeMode((prevMode) => {
      let newMode;
      if (prevMode === 'dark') {
        newMode = 'light';
        setGlobalTheme('light'); // Sync with next-themes
        showToast("Light mode activated", "info");
      } else if (prevMode === 'light') {
        setNewspaperView(true);
        setViewMode('newspaper');
        setCurrentArticleIndex(0);
        newMode = 'vintage';
        setGlobalTheme('dark'); // Keep global dark for vintage
        showToast("Vintage mode activated", "info");
      } else {
        setNewspaperView(false);
        newMode = 'dark';
        setGlobalTheme('dark'); // Sync with next-themes
        showToast("Dark mode activated", "info");
      }
      updateUrl({ theme: newMode, view: newMode === 'vintage' ? 'newspaper' : viewMode });
      return newMode;
    });
  };

  const handleViewModeChange = (mode) => {
    viewModeRef.current = mode;
    setViewMode(mode);
    setNewspaperView(mode === 'newspaper');
    setExpandedIndex(null);
    updateUrl({ view: mode });
    showToast(`${mode.charAt(0).toUpperCase() + mode.slice(1)} view activated`, "info");
    
    // Reset map view when changing view modes
    if (mode !== 'map' && mapView) {
      setMapView(false);
    } else if (mode === 'map') {
      setMapView(true);
    }
    // Reset lazy loading when switching to grid view
    if (mode === 'grid') {
      setVisibleGroupCount(INITIAL_VISIBLE_GROUP_COUNT); // Reset to initial batch size
    }
    // Switch to vintage theme for newspaper view
    if (mode === 'newspaper') {
      setThemeMode('vintage');
    }
    // Switch to dark theme for grid and timeline views
    if (mode === 'grid' || mode === 'timeline') {
      setThemeMode('dark');
    }
  };

  const updateUrl = (params) => {
    if (!URL_SYNC_ENABLED) return;
    // Only update query params when we're on planning updates and not navigating
    if (isRouting) return;
    if (isApplicationModalOpen) return;
    const runtimePath = typeof window !== 'undefined' ? window.location.pathname : router.pathname;
    if (!String(runtimePath || '').startsWith('/planning-updates')) return;
    const currentQuery = typeof window !== 'undefined'
      ? Object.fromEntries(new URLSearchParams(window.location.search))
      : stripRouteQueryKeys(router.query);
    const newQuery = { ...currentQuery, ...params };
    // If nothing changed, skip
    const keys = new Set([...Object.keys(currentQuery || {}), ...Object.keys(newQuery)]);
    let changed = false;
    for (const k of keys) {
      if (String(currentQuery?.[k] ?? '') !== String(newQuery?.[k] ?? '')) {
        changed = true; break;
      }
    }
    if (!changed) return;

    if (pendingUrlUpdateRef.current) {
      clearTimeout(pendingUrlUpdateRef.current);
    }

    pendingUrlUpdateRef.current = setTimeout(() => {
      // Avoid full router re-render by updating URL directly.
      if (typeof window !== 'undefined') {
        if (window.location.pathname !== runtimePath) return;
        const nextUrl = patchPlanningQuery(window.location.href, params);
        window.history.replaceState(window.history.state, '', nextUrl);
      } else {
        router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
      }
      pendingUrlUpdateRef.current = null;
    }, URL_SYNC_DEBOUNCE_MS);
  };

  const toggleAiSummaries = () => {
    setShowAiSummaries(!showAiSummaries);
    showToast(showAiSummaries ? "AI summaries hidden" : "AI summaries visible", "info");
  };

  // Note: Grouping mode handler removed - now using backend group_id

  const handleCardExpand = (index) => {
    setExpandedIndex(prevIndex => {
      const newIndex = prevIndex === index ? null : index;
      if (newIndex !== null) {
        showToast("Application details expanded", "info");
      }
      return newIndex;
    });
  };

  const toggleFavorite = useCallback((application) => {
    // Handle both application objects and reference strings for backward compatibility
    const applicationObj = typeof application === 'string'
      ? { reference_no: application, source_council: currentCouncilSlug }
      : application;
    if (!applicationObj || !applicationObj.reference_no) return;

    const ref = applicationObj.reference_no;
    const favoriteCouncil =
      applicationObj.source_council ||
      applicationObj.council_slug ||
      applicationObj.council ||
      currentCouncilSlug;
    const favoritePayload = {
      ...applicationObj,
      source_council: favoriteCouncil,
    };
    const favoriteKey = applicationIdentityKey(favoritePayload);
    const isFav = favorites.some(fav => applicationIdentityKey(fav) === favoriteKey);

    if (process.env.NEXT_PUBLIC_API_URL) {
      // Use userDataService for both API and local-only modes
      // It now handles optimistic updates properly
      if (isFav) {
        showToast("Removed from favorites", "info");
        userDataService.removeFavorite(ref, favoriteCouncil).catch((e) => {
          if (e?.status === 401) return; // handled by auth UI
          showToast('Network error: could not update favourite', 'warning');
        });
      } else {
        showToast("Added to favorites", "success");
        userDataService.addFavorite(favoritePayload).catch((e) => {
          if (e?.status === 401) return;
          showToast('Network error: could not update favourite', 'warning');
        });
      }
      // UI will update via favoritesUpdated event from userDataService
      return;
    }

    // Fallback: local-only behaviour with dedupe (same as before)
    setFavorites(prev => {
      const exists = prev.some(fav => applicationIdentityKey(fav) === favoriteKey);
      if (exists) {
        showToast("Removed from favorites", "info");
        return prev.filter(fav => applicationIdentityKey(fav) !== favoriteKey);
      } else {
        showToast("Added to favorites", "success");
        const withTimestamp = { ...favoritePayload };
        if (!withTimestamp.favorited_at) withTimestamp.favorited_at = new Date().toISOString();
        // Remove duplicates then add
        const withoutDup = prev.filter(f => applicationIdentityKey(f) !== favoriteKey);
        return [...withoutDup, withTimestamp];
      }
    });
  }, [currentCouncilSlug, favorites, setFavorites, showToast]);

  const toggleFilterByAddedToday = (todayDateString) => {
    setFilterByAddedToday(prev => {
      const newFilterState = prev ? false : todayDateString;
      if (newFilterState) {
        setFilterByScrapedDate(null);
        setFilterByAddedYesterday(false);
      }
      return newFilterState;
    });
  };

  const toggleFilterByAddedYesterday = (yesterdayDateString) => {
    setFilterByAddedYesterday(prev => {
      const newFilterState = prev ? false : yesterdayDateString;
      if (newFilterState) {
        setFilterByScrapedDate(null);
        setFilterByAddedToday(false);
      }
      return newFilterState;
    });
  };

  const handleSetFilterByScrapedDate = (date) => {
    setFilterByScrapedDate(date);
    if (date) {
      setFilterByAddedToday(false);
      setFilterByAddedYesterday(false);
    }
  };

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let filtered = scopedEffectiveAllApplications;

    // Contextual text search: address-ish tokens must match address/reference; keyword tokens can match anywhere
    const searchMatchers = buildPlanningSearchMatchers(searchQuery);
    if (searchMatchers.length > 0) {
      filtered = scopedApplicationSearchIndex
        .filter(({ addressRefText, fullText }) => {
          if (!fullText) return false;

          return searchMatchers.every((matcher) => {
            const target = matcher.addressish ? (addressRefText || fullText) : fullText;
            return matcher.pattern.test(target);
          });
        })
        .map(({ app }) => app);
    }

    // Main filter pills (e.g., 'New This Week')
    if (selectedFilter === "new") {
      filtered = filtered.filter(isApplicationNewThisWeek);
    } else if (selectedFilter === "recent") {
      filtered = filtered.filter(isApplicationNewThisWeek);
    } else if (selectedFilter === "favorites") {
      const favoriteRefs = new Set(favorites.map(fav => applicationIdentityKey(fav)));
      filtered = filtered.filter(app => favoriteRefs.has(applicationIdentityKey(app)));
    } else if (selectedFilter === "worth-watching") {
      filtered = filtered.filter(app => app?.interesting === true);
    } else if (selectedFilter === "reports") {
      // Filter to show only applications that have generated reports
      try {
        const storedReports = JSON.parse(localStorage.getItem('planning_reports') || '[]');
        const reportRefs = new Set(storedReports.map(r => r.reference_no));
        filtered = filtered.filter(app => reportRefs.has(app.reference_no));
      } catch (error) {
      console.warn('Failed to load reports for filtering', summarizeRecoverableError(error));
        filtered = []; // Show no results if reports can't be loaded
      }
    } else if (selectedFilter === "added-today") {
      filtered = filtered.filter(app => isPlanningFeedAddedToday(app, referenceDate));
    } else if (selectedFilter === "weekly-highlights") {
      // Filter for weekly highlights - applications from last 7 days with high interest scores
      const isWithinLastWeek = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return false;
        const now = referenceDate;
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return date >= weekAgo && date <= now;
      };

      filtered = filtered.filter(app => {
        const primary = getPreferredAddedDate(app);
        return isWithinLastWeek(primary);
      });

      // Interest score filtering handled in PlanningUpdatesPage; here we only limit by date
    } else if (selectedFilter === "trending") {
      // Filter for trending - combination of today's and weekly highlights
      const todaysApps = filtered.filter(app => isPlanningFeedAddedToday(app, referenceDate));

      const isWithinLastWeek = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = referenceDate;
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return date >= weekAgo && date <= now;
      };

      const weeklyApps = filtered.filter(app =>
        isWithinLastWeek(getPreferredAddedDate(app))
      );
      
      // Combine and deduplicate
      const combinedApps = [...todaysApps, ...weeklyApps];
      const uniqueApps = combinedApps.filter((app, index, arr) => 
        arr.findIndex(a => applicationIdentityKey(a || {}) === applicationIdentityKey(app || {})) === index
      );
      
      filtered = uniqueApps;
    }

    // Project Category filter - support both include and exclude modes, multiple categories
    if (multipleCategoryFilters && multipleCategoryFilters.length > 0) {
      const categorySet = new Set(multipleCategoryFilters);
      debugLog('🔍 DEBUG: Multi-category filtering:', categoryFilterMode, 'mode with categories:', Array.from(categorySet));
      
      filtered = filtered.filter(app => {
        // Use API-only categorization if app has no category
        if (!app.category) {
          const { primaryCategory } = apiOnlyCategorizeApplication(app);
          app.category = primaryCategory;
        }
        
        const cleanCategory = app.category.replace('__low', '');
        const hasCategory = categorySet.has(cleanCategory);
        
        // Include mode: show only apps with selected categories
        // Exclude mode: show apps that DON'T have selected categories
        return categoryFilterMode === 'include' ? hasCategory : !hasCategory;
      });
      
      debugLog('🔍 DEBUG: Applications after multi-category filter:', filtered.length);
    } else if (selectedCategoryFilter) {
      debugLog('🔍 DEBUG: Single category filtering:', categoryFilterMode, 'mode with category:', selectedCategoryFilter);
      debugLog('🔍 DEBUG: Total applications before filter:', filtered.length);
      
      filtered = filtered.filter(app => {
        // Use API-only categorization if app has no category
        if (!app.category) {
          const { primaryCategory } = apiOnlyCategorizeApplication(app);
          app.category = primaryCategory;
        }
        
        const cleanCategory = app.category.replace('__low', '');
        const matches = cleanCategory === selectedCategoryFilter;
        
        // Include mode: show only apps with selected category
        // Exclude mode: show apps that DON'T have selected category
        const result = categoryFilterMode === 'include' ? matches : !matches;
        
        // Debug logging for the selected category
        if (result && categoryFilterMode === 'include') {
          debugLog('✅ INCLUDE MATCH:', app.reference_no, '- Category:', cleanCategory);
        } else if (result && categoryFilterMode === 'exclude') {
          debugLog('✅ EXCLUDE MATCH:', app.reference_no, '- Category:', cleanCategory, '- Excluded category:', selectedCategoryFilter);
        }
        
        return result;
      });
      
      debugLog('🔍 DEBUG: Applications after single category filter:', filtered.length);
      const stats = getCategorizationStats(filtered);
      debugLog('🔍 DEBUG: Categorization stats for filtered results:', stats);
    }

    // Scale rating filter
    const rawScaleMin = Number(scaleRange?.min);
    const rawScaleMax = Number(scaleRange?.max);
    const normalizedScaleMin = Number.isFinite(rawScaleMin) ? rawScaleMin : 1;
    const normalizedScaleMax = Number.isFinite(rawScaleMax) ? rawScaleMax : 10;
    const boundedScaleMin = Math.max(1, Math.min(normalizedScaleMin, normalizedScaleMax));
    const boundedScaleMax = Math.min(10, Math.max(normalizedScaleMin, normalizedScaleMax));
    const hasScaleRangeFilter = boundedScaleMin > 1 || boundedScaleMax < 10;

    const scaleMap = {
      low: { min: 1, max: 3 },
      small: { min: 1, max: 3 },
      medium: { min: 4, max: 6 },
      high: { min: 7, max: 10 },
      large: { min: 7, max: 10 }
    };

    let effectiveScaleMin = hasScaleRangeFilter ? boundedScaleMin : null;
    let effectiveScaleMax = hasScaleRangeFilter ? boundedScaleMax : null;

    if (!hasScaleRangeFilter && selectedScaleCategory !== "all") {
      const presetRange = scaleMap[selectedScaleCategory] || { min: 1, max: 10 };
      effectiveScaleMin = presetRange.min;
      effectiveScaleMax = presetRange.max;
    }

    if (effectiveScaleMin !== null && effectiveScaleMax !== null) {
      filtered = filtered.filter(app => {
        const rating = Number(app.scale_rating);
        return Number.isFinite(rating) && rating >= effectiveScaleMin && rating <= effectiveScaleMax;
      });
    }

    const normalizedDecisionStatusFilter = typeof decisionStatusFilter === 'string'
      ? decisionStatusFilter.toLowerCase().trim()
      : 'all';

    if (normalizedDecisionStatusFilter && normalizedDecisionStatusFilter !== 'all') {
      filtered = filtered.filter((app) => {
        const decisionToken = getDecisionStatusToken(app);

        if (normalizedDecisionStatusFilter === 'decided') {
          return decisionToken === 'decided' || decisionToken === 'refused';
        }

        return decisionToken === normalizedDecisionStatusFilter;
      });
    }

    const getPreferredDateString = (value) => {
      if (!value) return null;

      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
      }

      if (typeof value === 'string') {
        if (value.length >= 10) {
          return value.slice(0, 10);
        }

        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toISOString().slice(0, 10);
        }
        return null;
      }

      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
      }

      return null;
    };

    // Date added filter (prefers API created_at timestamp)
    if (filterByScrapedDate) {
      filtered = filtered.filter(app => {
        const primaryDate = getPreferredDateString(app.created_at) || getPreferredDateString(app.scraped_date);
        return primaryDate === filterByScrapedDate;
      });
    }
    
    // This is a simple flag filter and should be efficient enough
    if (filterByAddedToday) {
      filtered = filtered.filter(app => isPlanningFeedAddedToday(app, referenceDate));
    }

    if (filterByAddedYesterday) {
      filtered = filtered.filter(app => isPlanningFeedAddedYesterday(app, referenceDate));
    }

    return filtered;
  }, [
    scopedEffectiveAllApplications,
    scopedApplicationSearchIndex,
    searchQuery, 
    selectedFilter, 
    favorites, 
    multipleCategoryFilters, 
    selectedCategoryFilter, 
    categoryFilterMode,
    selectedScaleCategory, 
    scaleRange,
    decisionStatusFilter,
    filterByScrapedDate,
    filterByAddedToday,
    filterByAddedYesterday,
    referenceDate
  ]);

  const processedApplications = useMemo(() => {
    if (!filteredApplications) return [];

    const sorted = [...filteredApplications];

    if (sortByDate) {
      sorted.sort((a, b) => {
        const dateA = new Date(getMostRelevantDate(a) || 0);
        const dateB = new Date(getMostRelevantDate(b) || 0);
        return dateB - dateA;
      });
    } else {
      // Optional: implement a different default sort, e.g., by reference number
      sorted.sort((a, b) => (a.reference_no || "").localeCompare(b.reference_no || ""));
    }
    
    return sorted;
  }, [filteredApplications, sortByDate]);

  const groupedApplications = useMemo(() => {
    if (!processedApplications) return [];
    return buildVisibleGroupedApplicationsCollection(processedApplications, {
      showOnlyGrouped: showOnlyGroupedApplications,
      visibleGroupCount,
    });
  }, [processedApplications, showOnlyGroupedApplications, visibleGroupCount]);

  const newThisWeekGroupCount = useMemo(() => {
    if (!Array.isArray(processedApplications) || processedApplications.length === 0) {
      return 0;
    }

    const recentApplications = processedApplications.filter(isApplicationNewThisWeek);
    if (recentApplications.length === 0) {
      return 0;
    }

    return buildGroupedApplicationsCollection(recentApplications).length;
  }, [processedApplications]);

  // Note: Frontend grouping mode removed - now using backend group_id

  // Hydrate and subscribe to favourites from server when Accounts API is configured
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!process.env.NEXT_PUBLIC_API_URL) return;

    let favoritesAlreadyLoaded = false;

    const onFavs = (e) => {
      try {
        const rows = e?.detail;
        const favorites = Array.isArray(rows)
          ? rows.filter((row) => {
              if (currentCouncilSlug === AGGREGATE_COUNCIL_SLUG) return isApplicationInPublicCouncil(row);
              const rowCouncil = getCouncilConfig(row?.source_council)?.slug;
              return !rowCouncil || rowCouncil === currentCouncilSlug;
            })
          : [];
        debugLog(`📥 Favorites updated from event: ${favorites.length} favorites`);
        setFavorites(favorites);
        favoritesAlreadyLoaded = favorites.length > 0;
      } catch (_) {
        setFavorites([]);
      }
    };

    // Optimistic events are no longer dispatched, so these handlers are not needed

    window.addEventListener('favoritesUpdated', onFavs);

    // Load favorites immediately if none have been loaded yet
    if (!favoritesAlreadyLoaded) {
      debugLog('🔄 PlanningContext starting favorites loading...');
      userDataService.hydrateFavorites(false, currentCouncilSlug).catch((e) => {
        // Handle authentication errors gracefully without throwing
        if (e?.status === 401) {
          console.debug('Authentication expired during favorites hydration in PlanningContext');
          return;
        }
        // Show toast for network errors only
        try { showToast('Unable to reach accounts API. Favourites not synced.', 'warning'); } catch (_error) { void _error; }
      });
    } else {
      debugLog('⏭️ Favorites already loaded or loading skipped');
    }

    return () => {
      window.removeEventListener('favoritesUpdated', onFavs);
    };
  }, [currentCouncilSlug, showToast]);

  // Persist favourites across sessions (local fallback only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NEXT_PUBLIC_API_URL) return;
    try {
      const stored = localStorage.getItem(getFavoritesStorageKey(currentCouncilSlug));
      if (stored) {
        const arr = JSON.parse(stored);
        const seen = new Set();
        const unique = Array.isArray(arr) ? arr.filter(i => {
          const r = i && applicationIdentityKey(i);
          if (!r) return false;
          if (seen.has(r)) return false;
          seen.add(r);
          return true;
        }) : [];
        setFavorites(unique);
      }
    } catch (err) {
      debugWarn('Failed to load favourites from localStorage', err);
    }
  }, [currentCouncilSlug]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NEXT_PUBLIC_API_URL) return;
    try {
      localStorage.setItem(getFavoritesStorageKey(currentCouncilSlug), JSON.stringify(favorites));
    } catch (err) {
      debugWarn('Failed to save favourites to localStorage', err);
    }
  }, [currentCouncilSlug, favorites]);

  // Load persisted theme mode
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedTheme = localStorage.getItem('cs_theme');
    if (storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'vintage') {
      setThemeMode(storedTheme);
    }
  }, []);

  // Save theme mode changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('cs_theme', themeMode);
  }, [themeMode]);

  // Sync with global theme when it changes externally
  useEffect(() => {
    if (globalTheme && themeMode !== 'vintage') {
      if (globalTheme === 'light' && themeMode !== 'light') {
        setThemeMode('light');
      } else if (globalTheme === 'dark' && themeMode !== 'dark') {
        setThemeMode('dark');
      }
    }
  }, [globalTheme, themeMode]);

  // Note: Theme CSS classes are now managed by UnifiedThemeProvider to prevent conflicts

  // Theme utility
  const getThemeClasses = () => {
    let textClass = '';
    let cardBgClass = '';
    let buttonPrimaryClass = '';
    let buttonSecondaryClass = '';
    let accentClass = '';
    let borderClass = '';
    
    if (themeMode === 'dark') {
      textClass = 'text-white';
      cardBgClass = 'bg-gray-800/90 backdrop-blur-sm';
      buttonPrimaryClass = 'bg-indigo-600 hover:bg-indigo-700 text-white';
      buttonSecondaryClass = 'bg-gray-700 hover:bg-gray-600 text-white';
      accentClass = 'text-indigo-400';
      borderClass = 'border-gray-700';
    } else if (themeMode === 'light') {
      textClass = 'text-gray-800';
      cardBgClass = 'bg-white/90 backdrop-blur-sm';
      buttonPrimaryClass = 'bg-indigo-500 hover:bg-indigo-600 text-white';
      buttonSecondaryClass = 'bg-gray-200 hover:bg-gray-300 text-gray-800';
      accentClass = 'text-indigo-600';
      borderClass = 'border-gray-200';
    } else if (themeMode === 'vintage') {
      textClass = 'text-gray-900';
      cardBgClass = 'bg-amber-100/90 backdrop-blur-sm';
      buttonPrimaryClass = 'bg-amber-700 hover:bg-amber-800 text-amber-50';
      buttonSecondaryClass = 'bg-amber-200 hover:bg-amber-300 text-amber-900';
      accentClass = 'text-amber-800';
      borderClass = 'border-amber-300';
    }
    
    return { 
      textClass, 
      cardBgClass, 
      buttonPrimaryClass, 
      buttonSecondaryClass, 
      accentClass, 
      borderClass 
    };
  };

  return (
    <PlanningContext.Provider
      value={{
        // Animation controls
        hasAnimated,
        setHasAnimated,
        // Theme and view states
        themeMode,
        setThemeMode,
        cycleThemeMode,
        viewMode,
        handleViewModeChange, 
        newspaperView,
        setNewspaperView, 
        // Data states
        applications: processedApplications,
        allApplications: scopedEffectiveAllApplications,
        councilSlug: currentCouncilSlug,
        areaId: currentAreaId,
        councilConfig,
        switchCouncil,
        referenceDate,
        feedSummary,
        usesRemoteSummary,
        groupedApplications,
        newThisWeekGroupCount,
        isLoading,
        isLoadingFullDataset: _isLoadingFullDataset,
        isCategorizing,
        // Two-stream categorization state
        categorizedApps,
        uncategorizedApps,
        backgroundProcessing,
        processingProgress,
        // UI states
        selectedImage,
        handleSetSelectedImage, 
        expandedIndex,
        handleCardExpand, 
        showAiSummaries,
        toggleAiSummaries, 
        selectedFilter,
        setSelectedFilter,
        decisionStatusFilter,
        setDecisionStatusFilter,
        searchQuery,
        setSearchQuery,
        sortByDate,
        setSortByDate,
        showFilterPanel,
        setShowFilterPanel,
        currentArticleIndex,
        setCurrentArticleIndex,
        showNotification,
        notificationMessage,
        notificationType,
        showToast, 
        isSidebarOpen,
        setIsSidebarOpen,
        initialScreenCheckDone,
        setInitialScreenCheckDone,
        showRecents,
        setShowRecents,
        recentSearches,
        setRecentSearches,
        favorites,
        toggleFavorite,
        mapView, 
        setMapView,
        // Scale rating category
        selectedScaleCategory,
        setSelectedScaleCategory,
        activeTab,
        setActiveTab,
        // Advanced scale filter
        advancedScaleMode,
        setAdvancedScaleMode,
        scaleRange,
        setScaleRange,
        // Project category filter
        selectedCategoryFilter,
        setSelectedCategoryFilter,
        includeSecondaryCategories,
        setIncludeSecondaryCategories,
        multipleCategoryFilters,
        setMultipleCategoryFilters,
        categoryFilterMode,
        setCategoryFilterMode,
        // Grouped applications filter
        showOnlyGroupedApplications,
        setShowOnlyGroupedApplications,
        // Note: Grouping mode removed - now using backend group_id
        // Navbar visibility
        navbarVisible,
        // Batch loading state
        visibleGroupCount,
        setVisibleGroupCount,
        hasLoaded,
        // Theme classes
        getThemeClasses, 
        // Utility methods
        updateUrl,
        isNewThisWeek,
        isApplicationNewThisWeek,
        filterByAddedToday,
        toggleFilterByAddedToday,
        filterByAddedYesterday,
        toggleFilterByAddedYesterday,
        filterByScrapedDate,
        setFilterByScrapedDate: handleSetFilterByScrapedDate,
        // Modal state and handlers
        selectedApplication,
        isApplicationModalOpen,
        openApplicationModal,
        closeApplicationModal,
        initialModalTab,
        initialModalDocumentUrl,
        initialModalImageExpanded
      }}
    >
      {children}
    </PlanningContext.Provider>
  );
};

export const usePlanningContext = () => useContext(PlanningContext);

export default PlanningContext;
